import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  PlayIcon,
  PhotoIcon,
  ChatBubbleLeftRightIcon,
  ShieldCheckIcon,
  ArrowPathIcon,
  StopIcon,
  SparklesIcon,
  UserGroupIcon,
  ChevronDownIcon,
  PauseIcon,
  ClockIcon,
  TrashIcon,
  CircleStackIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';
import { useSocket } from '../contexts/SocketContext';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { api } from '../services/api';
import toast from 'react-hot-toast';
import { useDebounce } from '../hooks/useDebounce';
import { applyScraperProgressBatchToSessions } from '../utils/applyScraperProgressToSession';
import { reconcileScraperSessionProgress } from '../utils/reconcileScraperSessionProgress';
import ScraperPipelinePanel from '../components/ScraperPipelinePanel';

/** Rows per HTTP request for Extract all / large Extract new (reduces HTTP/2 errors and huge single blobs). */
const RECIPIENT_EXPORT_CHUNK_ROWS = 50_000;

/** Default whitelist — must match strings returned by X AboutAccountQuery (account_based_in). */
const DEFAULT_SCRAPER_COUNTRY_WHITELIST = [
  'Australia', 'Austria', 'Belgium', 'Canada', 'Denmark', 'Finland', 'France', 'Germany',
  'Ireland', 'Italy', 'Luxembourg', 'Netherlands', 'New Zealand', 'Norway', 'Spain',
  'Switzerland', 'United Kingdom', 'United States', 'Poland',
  'Portugal', 'North America', 'Monaco', 'Malta'
];

/** Merge API session list with prior client-only fields (socket queue stats, checker status). */
function mergePreservedSessionFields(prevSessions, nextSessions) {
  const prevById = new Map((prevSessions || []).map((s) => [s.sessionId, s]));
  return (nextSessions || []).map((session) => {
    const prev = prevById.get(session.sessionId);
    return reconcileScraperSessionProgress({
      ...session,
      accountsInCooldown: session.accountsInCooldown || 0,
      activeCheckerStatus: prev?.activeCheckerStatus ?? session.activeCheckerStatus,
      countryQueueStats: prev?.countryQueueStats ?? session.countryQueueStats,
      activityQueueStats: prev?.activityQueueStats ?? session.activityQueueStats
    });
  });
}

/** Poll interval when tasks are running (main API reads Mongo; works without Socket.IO Redis adapter on scraper). */
const SCRAPER_PROGRESS_POLL_MS = Math.max(
  3000,
  Number(process.env.REACT_APP_SCRAPER_PROGRESS_POLL_MS) || 10000
);

/* ------------------------------------------------------------------ */
/*  Presentational helpers for the redesigned scrape-task card.       */
/*  - ScraperActionButton: unified button shape/size for the card.    */
/*  - SessionStatusChip: pill for session.status.                     */
/*  - ScraperPipelineStages: cards-based pipeline portrait.           */
/* ------------------------------------------------------------------ */

function _percent(num, den) {
  if (!den || den <= 0) return 0;
  return Math.max(0, Math.min(100, Math.round((num / den) * 100)));
}

function _fmt(n) {
  const x = Number(n || 0);
  return Number.isFinite(x) ? x.toLocaleString() : '0';
}

function ScraperActionButton({ onClick, disabled, tone = 'slate', icon: Icon, children, title, type = 'button' }) {
  const tones = {
    slate:
      'text-slate-700 border-slate-300 hover:bg-slate-100/70 dark:text-slate-200 dark:border-slate-600 dark:hover:bg-slate-700/40',
    emerald:
      'text-emerald-700 border-emerald-300 hover:bg-emerald-100/70 dark:text-emerald-300 dark:border-emerald-700 dark:hover:bg-emerald-900/30',
    yellow:
      'text-yellow-700 border-yellow-300 hover:bg-yellow-100/70 dark:text-yellow-300 dark:border-yellow-700 dark:hover:bg-yellow-900/30',
    red:
      'text-red-700 border-red-300 hover:bg-red-100/70 dark:text-red-300 dark:border-red-700 dark:hover:bg-red-900/30',
    blue:
      'text-blue-700 border-blue-300 hover:bg-blue-100/70 dark:text-blue-300 dark:border-blue-700 dark:hover:bg-blue-900/30',
    violet:
      'text-violet-700 border-violet-300 hover:bg-violet-100/70 dark:text-violet-300 dark:border-violet-700 dark:hover:bg-violet-900/30'
  };
  const cls = [
    'inline-flex items-center gap-1.5 h-8 px-3 rounded-xl text-xs font-medium border backdrop-blur-sm',
    'transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap',
    tones[tone] || tones.slate
  ].join(' ');
  return (
    <button type={type} onClick={onClick} disabled={disabled} title={title} className={cls}>
      {Icon && <Icon className="h-3.5 w-3.5" />}
      {children}
    </button>
  );
}

function SessionStatusChip({ status }) {
  const base = 'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium backdrop-blur-sm';
  if (status === 'running') {
    return (
      <span className={`${base} bg-blue-100/80 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300`}>
        <span className="h-1.5 w-1.5 rounded-full bg-blue-500 animate-pulse" />
        Running
      </span>
    );
  }
  if (status === 'paused') {
    return <span className={`${base} bg-yellow-100/80 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300`}>Paused</span>;
  }
  if (status === 'completed') {
    return <span className={`${base} bg-emerald-100/80 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300`}>Completed</span>;
  }
  if (status === 'stopped' || status === 'failed') {
    return <span className={`${base} bg-red-100/80 text-red-700 dark:bg-red-900/30 dark:text-red-300`}>{status === 'failed' ? 'Failed' : 'Stopped'}</span>;
  }
  return <span className={`${base} bg-slate-100/80 text-slate-700 dark:bg-slate-700/70 dark:text-slate-200`}>{status || 'Idle'}</span>;
}

/** One stage card in the pipeline portrait (glass look, step badge, optional bar). */
function StageCard({ stepNumber, title, subtitle, accent = 'slate', bar, stats, footer }) {
  const accents = {
    blue: 'from-blue-50/80 to-white/60 dark:from-blue-900/20 dark:to-slate-900/30 border-blue-200/60 dark:border-blue-800/60',
    indigo: 'from-indigo-50/80 to-white/60 dark:from-indigo-900/20 dark:to-slate-900/30 border-indigo-200/60 dark:border-indigo-800/60',
    violet: 'from-violet-50/80 to-white/60 dark:from-violet-900/20 dark:to-slate-900/30 border-violet-200/60 dark:border-violet-800/60',
    emerald: 'from-emerald-50/80 to-white/60 dark:from-emerald-900/20 dark:to-slate-900/30 border-emerald-200/60 dark:border-emerald-800/60',
    slate: 'from-slate-50/80 to-white/60 dark:from-slate-800/40 dark:to-slate-900/30 border-slate-200/60 dark:border-slate-700/60'
  };
  const badge = {
    blue: 'bg-blue-500/90',
    indigo: 'bg-indigo-500/90',
    violet: 'bg-violet-500/90',
    emerald: 'bg-emerald-500/90',
    slate: 'bg-slate-500/90'
  };
  return (
    <div className={`relative rounded-2xl p-3.5 border backdrop-blur-xl bg-gradient-to-br shadow-sm ${accents[accent] || accents.slate}`}>
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className={`flex items-center justify-center h-5 w-5 rounded-full text-white text-[10px] font-bold shadow ${badge[accent] || badge.slate}`}>
            {stepNumber}
          </span>
          <div className="min-w-0">
            <div className="text-xs font-semibold text-slate-800 dark:text-slate-100 truncate">{title}</div>
            {subtitle && <div className="text-[10px] text-slate-500 dark:text-slate-400 truncate">{subtitle}</div>}
          </div>
        </div>
      </div>
      {bar && (
        <div className="mb-2.5">
          <div className="flex items-center justify-between text-[10px] mb-1">
            <span className="text-slate-500 dark:text-slate-400">{bar.label}</span>
            <span className="font-semibold text-slate-700 dark:text-slate-200">{bar.percent}%</span>
          </div>
          <div className="h-1.5 w-full rounded-full bg-white/60 dark:bg-slate-800/70 overflow-hidden">
            <div className={`h-full rounded-full transition-all duration-500 ${bar.barColor}`} style={{ width: `${bar.percent}%` }} />
          </div>
        </div>
      )}
      {stats && stats.length > 0 && (
        <div className="grid grid-cols-2 gap-2">
          {stats.map((s, i) => {
            const toneClass = {
              slate: 'text-slate-700 dark:text-slate-200',
              emerald: 'text-emerald-600 dark:text-emerald-400',
              indigo: 'text-indigo-600 dark:text-indigo-400',
              blue: 'text-blue-600 dark:text-blue-400',
              violet: 'text-violet-600 dark:text-violet-400',
              orange: 'text-orange-600 dark:text-orange-400',
              red: 'text-red-600 dark:text-red-400',
              gray: 'text-slate-500 dark:text-slate-400'
            }[s.tone || 'slate'];
            return (
              <div key={i} className="flex flex-col">
                <span className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">{s.label}</span>
                <span className={`text-sm font-semibold tabular-nums ${toneClass}`}>{_fmt(s.value)}</span>
              </div>
            );
          })}
        </div>
      )}
      {footer && (
        <div className="mt-2 pt-2 border-t border-slate-200/60 dark:border-slate-700/60 text-[10px] text-slate-500 dark:text-slate-400">
          {footer}
        </div>
      )}
    </div>
  );
}

/** Cards-based pipeline portrait: Fetch → Country? → Activity? → Stored. */
function ScraperPipelineStages({ session }) {
  const p = session.progress || {};
  const countryEnabled = !!session.filters?.filterCountryWhitelist;
  const activityEnabled = !!session.filters?.filterActiveLastMonth;

  const overallPercent = _percent(p.totalScraped, p.totalFollowers);
  const countryPassPercent = _percent(p.totalCountryAccepted, p.totalCountryChecked);
  const activityPassPercent = _percent(p.totalActive, p.totalActivityChecked);

  const stageCount = 2 + (countryEnabled ? 1 : 0) + (activityEnabled ? 1 : 0);

  const gridCls =
    stageCount === 4
      ? 'grid gap-3 grid-cols-1 md:grid-cols-2 lg:grid-cols-4'
      : stageCount === 3
      ? 'grid gap-3 grid-cols-1 md:grid-cols-3'
      : 'grid gap-3 grid-cols-1 md:grid-cols-2';

  let step = 1;
  return (
    <div className={gridCls}>
      <StageCard
        stepNumber={step++}
        title="Fetch"
        subtitle="Raw rows from X"
        accent="blue"
        bar={{ label: 'Scrape progress', percent: overallPercent, barColor: 'bg-gradient-to-r from-blue-400 to-blue-500' }}
        stats={[
          { label: 'Fetched', value: p.totalScraped, tone: 'blue' },
          { label: 'Duplicates', value: p.totalDuplicates, tone: 'orange' },
          { label: 'Skipped', value: p.totalSkipped, tone: 'gray' },
          ...(session.useBlacklist ? [{ label: 'Blacklisted', value: p.totalBlacklisted, tone: 'red' }] : [])
        ]}
      />
      {countryEnabled && (
        <StageCard
          stepNumber={step++}
          title="Country"
          subtitle="Whitelist check"
          accent="indigo"
          bar={{ label: 'Pass rate', percent: countryPassPercent, barColor: 'bg-gradient-to-r from-indigo-400 to-indigo-500' }}
          stats={[
            { label: 'Checked', value: p.totalCountryChecked, tone: 'indigo' },
            { label: 'Match', value: p.totalCountryAccepted, tone: 'emerald' },
            { label: 'Not in list', value: p.totalCountryRejected, tone: 'gray' },
            { label: 'Failed', value: p.totalCountryFailed, tone: 'red' }
          ]}
          footer={
            session.countryQueueStats && ['running', 'paused'].includes(session.status)
              ? `Queue: ${_fmt(session.countryQueueStats.waiting ?? 0)} waiting · ${_fmt(session.countryQueueStats.active ?? 0)} active`
              : null
          }
        />
      )}
      {activityEnabled && (
        <StageCard
          stepNumber={step++}
          title="Activity"
          subtitle="Last month"
          accent="violet"
          bar={{ label: 'Pass rate', percent: activityPassPercent, barColor: 'bg-gradient-to-r from-violet-400 to-violet-500' }}
          stats={[
            { label: 'Checked', value: p.totalActivityChecked, tone: 'violet' },
            { label: 'Active', value: p.totalActive, tone: 'emerald' },
            { label: 'Inactive', value: p.totalInactive, tone: 'gray' },
            { label: 'Failed', value: p.totalFailed, tone: 'red' }
          ]}
          footer={
            session.activityQueueStats && ['running', 'paused'].includes(session.status)
              ? `Queue: ${_fmt(session.activityQueueStats.waiting ?? 0)} waiting · ${_fmt(session.activityQueueStats.active ?? 0)} active`
              : null
          }
        />
      )}
      <StageCard
        stepNumber={step++}
        title="Stored"
        subtitle="Final dataset"
        accent="emerald"
        bar={{ label: 'vs fetched', percent: _percent(p.totalStored, p.totalScraped), barColor: 'bg-gradient-to-r from-emerald-400 to-emerald-500' }}
        stats={[{ label: 'Stored', value: p.totalStored, tone: 'emerald' }]}
      />
    </div>
  );
}

const Scraper = () => {
  const { user } = useAuth();
  const { isDarkMode } = useTheme();
  const [showStartModal, setShowStartModal] = useState(false);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [activeSessions, setActiveSessions] = useState([]);
  const [allSessions, setAllSessions] = useState([]);
  const [sessionFilter, setSessionFilter] = useState('active'); // 'active', 'all', 'running', 'paused', 'completed', 'stopped'
  const [activeTab, setActiveTab] = useState('sessions'); // 'sessions' | 'datasets' | 'followers'
  const [dataSets, setDataSets] = useState([]);
  const [loadingDataSets, setLoadingDataSets] = useState(false);
  const dataSetsStatsAbortRef = useRef(null);
  const [expandedDataSetTags, setExpandedDataSetTags] = useState(new Set());
  const [sourceUsernamesByTag, setSourceUsernamesByTag] = useState({});
  const [sourceSearchDraftByTag, setSourceSearchDraftByTag] = useState({});
  const [accounts, setAccounts] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [expandedActiveCheckerGroups, setExpandedActiveCheckerGroups] = useState(new Set());
  const [expandedUsernameDetails, setExpandedUsernameDetails] = useState(new Set());
  const [expandedSourcingLog, setExpandedSourcingLog] = useState(new Set());
  const [sourcingDecisionsBySession, setSourcingDecisionsBySession] = useState({});
  const [sourcingLogLoading, setSourcingLogLoading] = useState({});
  /** `${sessionId}|${username}` -> loading scrape-from-log */
  const [sourcingScrapeLoading, setSourcingScrapeLoading] = useState({});
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  /** Phase 5: aggregated pipeline health broadcast by the backend every 3 s */
  const [pipelineHealth, setPipelineHealth] = useState(null);
  /** Rolling window of per-session funnel samples for throughput estimation. */
  const [pipelineHistory, setPipelineHistory] = useState([]);
  /**
   * Per-replica claim map: instanceId → { country, activity, replicaLabel, ts }.
   * Each Railway replica emits its own health tick every 3 s. Without this
   * the UI flickers between the 3 replicas' "claims on this replica"
   * numbers. Entries older than REPLICA_CLAIM_TTL_MS are pruned so a
   * dead replica disappears from the display.
   */
  const [replicaClaims, setReplicaClaims] = useState({});

  /** Usernames tab: X accounts you scraped followers from (source usernames), not individual followers */
  const [scrapeSourcesList, setScrapeSourcesList] = useState([]);
  const [scrapeSourcesLoading, setScrapeSourcesLoading] = useState(false);
  const [scrapeSourcesPage, setScrapeSourcesPage] = useState(1);
  const [scrapeSourcesPagination, setScrapeSourcesPagination] = useState({
    total: 0,
    pages: 1,
    limit: 50
  });
  const [scrapeSourcesTagFilter, setScrapeSourcesTagFilter] = useState('');
  const [deletingScrapeSourceId, setDeletingScrapeSourceId] = useState(null);
  /** Lowercase source username strings for multi-select */
  const [selectedScrapeSourceKeys, setSelectedScrapeSourceKeys] = useState([]);
  const [bulkDeletingScrapeSources, setBulkDeletingScrapeSources] = useState(false);
  const [scrapeSourcesSelectAllMatchingLoading, setScrapeSourcesSelectAllMatchingLoading] = useState(false);
  const scrapeSourcesSelectAllRef = useRef(null);
  const scraperSessionsPollRef = useRef({
    activeSessions: [],
    allSessions: [],
    sessionFilter: 'active'
  });
  const [deletingSessionId, setDeletingSessionId] = useState(null);

  // Modal form state
  const [usernames, setUsernames] = useState('');
  const [taskName, setTaskName] = useState(() => {
    // Pre-fill with today's date in DD-MM-YYYY format
    const today = new Date();
    const day = String(today.getDate()).padStart(2, '0');
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const year = today.getFullYear();
    return `${day}-${month}-${year}`;
  });
  const [tag, setTag] = useState('');
  const [count, setCount] = useState(100);
  const [selectedAccountIds, setSelectedAccountIds] = useState([]);
  const [activeCheckerAccounts, setActiveCheckerAccounts] = useState([]);
  const [selectedActiveCheckerAccountIds, setSelectedActiveCheckerAccountIds] = useState([]);
  const [sourceAccounts, setSourceAccounts] = useState([]);
  const [selectedSourceAccountIds, setSelectedSourceAccountIds] = useState([]);
  const [expandedSourceGroups, setExpandedSourceGroups] = useState(new Set());
  const [sourcingEnabled, setSourcingEnabled] = useState(false);
  const [sourcingConfig, setSourcingConfig] = useState({
    grokFilter: true,
    containsLinks: false,
    bioWhitelist: false,
    minFollowers: 5000,
    maxFollowers: 500000,
    maxPendingTargets: 20
  });
  const [filters, setFilters] = useState({
    filterAvatar: false,
    filterDMs: false,
    filterVerified: false,
    filterActiveLastMonth: false,
    filterCountryWhitelist: false
  });
  const [allowedCountriesList, setAllowedCountriesList] = useState(() => [...DEFAULT_SCRAPER_COUNTRY_WHITELIST]);
  const [countryAddInput, setCountryAddInput] = useState('');
  const [useBlacklist, setUseBlacklist] = useState(false);

  const { socket } = useSocket();

  // Username validation state
  const [usernameValidation, setUsernameValidation] = useState({
    checking: false,
    errors: [],
    warnings: [],
    currentlyScraping: []
  });

  // Debounce usernames for validation
  const debouncedUsernames = useDebounce(usernames, 500);

  // Define functions before useEffects
  const loadActiveSessions = useCallback(async () => {
    try {
      const response = await api.get('/scraper/sessions?status=active');
      if (response.data.success) {
        setActiveSessions((prev) => mergePreservedSessionFields(prev, response.data.sessions));
      }
    } catch (error) {
      console.error('Error loading active sessions:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadAllSessions = useCallback(async (filter = 'all') => {
    try {
      const response = await api.get(`/scraper/sessions?status=${filter}`);
      if (response.data.success) {
        setAllSessions((prev) => mergePreservedSessionFields(prev, response.data.sessions));
        if (filter === 'active') {
          setActiveSessions((prev) => mergePreservedSessionFields(prev, response.data.sessions));
        }
      }
    } catch (error) {
      console.error('Error loading sessions:', error);
    }
  }, []);

  const loadAccounts = async () => {
    try {
      const response = await api.get('/scraper/accounts');
      if (response.data.success) {
        // Only show accounts with valid session (backend already filters by status 'active')
        setAccounts(response.data.accounts.filter(acc => acc.hasValidSession === true));
      }
    } catch (error) {
      console.error('Error loading accounts:', error);
    }
  };

  const loadSourceAccounts = async () => {
    try {
      const response = await api.get('/scraper/source-accounts');
      if (response.data.success) {
        setSourceAccounts(response.data.accounts);
      }
    } catch (error) {
      console.error('Error loading source accounts:', error);
    }
  };

  const loadActiveCheckerAccounts = async () => {
    try {
      const response = await api.get('/scraper/active-checker-accounts');
      if (response.data.success) {
        // Show all accounts with active_checker tag (not just those with valid sessions)
        // The backend will validate session data when the task starts
        setActiveCheckerAccounts(response.data.accounts);
      }
    } catch (error) {
      console.error('Error loading active checker accounts:', error);
      toast.error('Failed to load active checker accounts');
    }
  };

  const loadStats = useCallback(async () => {
    try {
      const response = await api.get('/scraper/stats');
      if (response.data.success) {
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  }, []);

  const fetchSourcingDecisions = useCallback(async (sessionId) => {
    setSourcingLogLoading((l) => ({ ...l, [sessionId]: true }));
    try {
      const res = await api.get(`/scraper/sessions/${encodeURIComponent(sessionId)}/sourcing-decisions`, {
        params: { limit: 200 }
      });
      if (res.data?.success) {
        setSourcingDecisionsBySession((prev) => ({ ...prev, [sessionId]: res.data.decisions || [] }));
      }
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || 'Failed to load sourcing log');
    } finally {
      setSourcingLogLoading((l) => ({ ...l, [sessionId]: false }));
    }
  }, []);

  const handleToggleSourcingLog = (sessionId) => {
    setExpandedSourcingLog((prev) => {
      const next = new Set(prev);
      const wasOpen = next.has(sessionId);
      if (wasOpen) {
        next.delete(sessionId);
      } else {
        next.add(sessionId);
        queueMicrotask(() => {
          fetchSourcingDecisions(sessionId);
        });
      }
      return next;
    });
  };

  const sourcingScrapeKey = (sessionId, username) => `${sessionId}|${String(username || '').toLowerCase()}`;

  const handleScrapeFromSourcingDecision = async (sessionId, username) => {
    const u = String(username || '').trim();
    if (!u) return;
    const key = sourcingScrapeKey(sessionId, u);
    setSourcingScrapeLoading((prev) => ({ ...prev, [key]: true }));
    try {
      const response = await api.post(`/scraper/sessions/${encodeURIComponent(sessionId)}/add-usernames`, {
        usernames: [u]
      });
      if (response.data?.success) {
        if (response.data.added > 0) {
          toast.success(`@${u} added to scrape queue`);
          loadActiveSessions();
          loadAllSessions(sessionFilter);
        } else {
          toast.success(response.data.message || 'All usernames already exist in this session');
        }
      } else {
        toast.error(response.data?.error || 'Failed to add username');
      }
    } catch (error) {
      console.error('Error adding username from sourcing log:', error);
      toast.error(error.response?.data?.error || error.response?.data?.message || 'Failed to add username');
    } finally {
      setSourcingScrapeLoading((prev) => ({ ...prev, [key]: false }));
    }
  };

  const refreshDataSetStats = useCallback(async (tag, signal) => {
    if (!tag) return;
    try {
      const statsRes = await api.get(`/scraper/data/${encodeURIComponent(tag)}/stats`, {
        timeout: 30_000,
        signal
      });
      if (statsRes.data?.success) {
        setDataSets((prev) => prev.map((row) => {
          if (row.tag !== tag) return row;
          const { success, ...rest } = statsRes.data;
          return { ...row, ...rest, statsLoading: false };
        }));
      }
    } catch (err) {
      if (signal?.aborted || err.code === 'ERR_CANCELED') return;
      setDataSets((prev) => prev.map((row) => (
        row.tag === tag ? { ...row, statsLoading: false, statsPartial: true } : row
      )));
    }
  }, []);

  const loadDataSets = useCallback(async () => {
    dataSetsStatsAbortRef.current?.abort();
    const ac = new AbortController();
    dataSetsStatsAbortRef.current = ac;

    setLoadingDataSets(true);
    try {
      const response = await api.get('/scraper/data', { timeout: 30_000, signal: ac.signal });
      if (!response.data.success) {
        toast.error('Failed to load data sets');
        return;
      }

      const shells = (response.data.dataSets || []).map((ds) => ({
        ...ds,
        statsLoading: true,
        totalCount: null,
        storedCount: null,
        canDmCount: null,
        verifiedCount: null,
        withAvatarCount: null,
        activeCount: null,
        pendingCount: null,
        rejectedCount: null,
        sourceUsernameCount: null,
        statsPartial: false
      }));
      setDataSets(shells);
      setLoadingDataSets(false);

      // Load stats one tag at a time so cards appear immediately and heavy
      // aggregations never block the whole tab.
      for (const ds of shells) {
        if (ac.signal.aborted) break;
        await refreshDataSetStats(ds.tag, ac.signal);
      }
    } catch (error) {
      if (error.code === 'ERR_CANCELED') return;
      console.error('Error loading data sets:', error);
      const msg = error.code === 'ECONNABORTED'
        ? 'Loading data sets timed out — try again in a moment'
        : (error.response?.data?.error || 'Failed to load data sets');
      toast.error(msg);
    } finally {
      if (!ac.signal.aborted) setLoadingDataSets(false);
    }
  }, [refreshDataSetStats]);

  const loadScrapeSources = useCallback(async () => {
    setScrapeSourcesLoading(true);
    try {
      const params = { page: scrapeSourcesPage, limit: 50 };
      const tagTrim = scrapeSourcesTagFilter.trim();
      if (tagTrim) {
        params.tag = tagTrim;
      }
      const response = await api.get('/scraper/scrape-sources', { params });
      if (response.data.success) {
        setScrapeSourcesList(response.data.items || []);
        setScrapeSourcesPagination(
          response.data.pagination || { total: 0, pages: 1, limit: 50 }
        );
      }
    } catch (error) {
      console.error('Error loading scrape sources:', error);
      toast.error(error.response?.data?.error || 'Failed to load scrape sources');
    } finally {
      setScrapeSourcesLoading(false);
    }
  }, [scrapeSourcesPage, scrapeSourcesTagFilter]);

  const loadSourceUsernamesForTag = useCallback(async (tag, { page = 1, search = '' } = {}) => {
    setSourceUsernamesByTag((prev) => ({
      ...prev,
      [tag]: { ...prev[tag], loading: true, error: null }
    }));
    try {
      const response = await api.get(`/scraper/data/${encodeURIComponent(tag)}/source-usernames`, {
        params: { page, limit: 50, search: search.trim() }
      });
      if (response.data.success) {
        setSourceUsernamesByTag((prev) => ({
          ...prev,
          [tag]: {
            loading: false,
            items: response.data.items || [],
            total: response.data.total ?? 0,
            page: response.data.page ?? 1,
            limit: response.data.limit ?? 50,
            searchApplied: search.trim(),
            error: null
          }
        }));
      } else {
        setSourceUsernamesByTag((prev) => ({
          ...prev,
          [tag]: {
            ...prev[tag],
            loading: false,
            error: response.data.error || 'Failed to load'
          }
        }));
      }
    } catch (error) {
      setSourceUsernamesByTag((prev) => ({
        ...prev,
        [tag]: {
          ...prev[tag],
          loading: false,
          error: error.response?.data?.error || error.message || 'Failed to load'
        }
      }));
    }
  }, []);

  const toggleDataSetSourcesExpanded = (tag) => {
    setExpandedDataSetTags((prev) => {
      const next = new Set(prev);
      if (next.has(tag)) {
        next.delete(tag);
      } else {
        next.add(tag);
        const draft = sourceSearchDraftByTag[tag] ?? '';
        loadSourceUsernamesForTag(tag, { page: 1, search: draft });
      }
      return next;
    });
  };

  const handleDeleteSourceUsernameRow = async (tag, sourceUsername, followerCount) => {
    const srcLabel = sourceUsername ? `@${sourceUsername}` : 'followers with unknown source';
    if (
      !window.confirm(
        `Delete ${Number(followerCount).toLocaleString()} stored follower(s) from ${srcLabel} for tag "${tag}"? This cannot be undone.`
      )
    ) {
      return;
    }
    const pathSeg =
      sourceUsername === null || sourceUsername === '' || sourceUsername === undefined
        ? '__none__'
        : encodeURIComponent(sourceUsername);
    try {
      const response = await api.delete(
        `/scraper/data/${encodeURIComponent(tag)}/source-usernames/${pathSeg}`,
        { timeout: 0 }
      );
      if (response.data.success) {
        toast.success(`Deleted ${response.data.deletedCount.toLocaleString()} follower(s)`);
        loadStats().catch(() => {});
        refreshDataSetStats(tag);
        const st = sourceUsernamesByTag[tag];
        loadSourceUsernamesForTag(tag, {
          page: st?.page || 1,
          search: st?.searchApplied ?? ''
        });
      } else {
        toast.error(response.data.error || 'Delete failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Delete failed');
    }
  };

  const handleDeleteDataSet = async (tag) => {
    if (
      !window.confirm(
        `Delete all scraped data for tag "${tag}"?\n\nThis removes stored followers AND associated scrape sessions for this tag. This cannot be undone.`
      )
    ) {
      return;
    }

    // Optimistically remove the dataset from UI immediately
    const deletedDataSet = dataSets.find(ds => ds.tag === tag);
    setDataSets(prev => prev.filter(ds => ds.tag !== tag));
    setExpandedDataSetTags((prev) => {
      const next = new Set(prev);
      next.delete(tag);
      return next;
    });
    setSourceUsernamesByTag((prev) => {
      const next = { ...prev };
      delete next[tag];
      return next;
    });
    setSourceSearchDraftByTag((prev) => {
      const next = { ...prev };
      delete next[tag];
      return next;
    });

    try {
      const response = await api.delete(`/scraper/data/${encodeURIComponent(tag)}`, {
        timeout: 0
      });
      if (response.data.success) {
        const sessionsRemoved = response.data.sessionsDeleted ?? 0;
        const msg = sessionsRemoved > 0
          ? `Deleted ${response.data.deletedCount.toLocaleString()} followers and ${sessionsRemoved} session(s) for tag "${tag}"`
          : `Deleted ${response.data.deletedCount.toLocaleString()} followers for tag "${tag}"`;
        toast.success(msg);
        loadStats().catch(err => console.error('Error reloading stats:', err));
      } else {
        // Restore the dataset if deletion failed
        if (deletedDataSet) {
          setDataSets(prev => [...prev, deletedDataSet].sort((a, b) => {
            // Sort by latestScrape date (newest first)
            const dateA = a.latestScrape ? new Date(a.latestScrape).getTime() : 0;
            const dateB = b.latestScrape ? new Date(b.latestScrape).getTime() : 0;
            return dateB - dateA;
          }));
        }
        toast.error(response.data.error || 'Failed to delete data set');
      }
    } catch (error) {
      // Restore the dataset if deletion failed
      if (deletedDataSet) {
        setDataSets(prev => [...prev, deletedDataSet].sort((a, b) => {
          // Sort by latestScrape date (newest first)
          const dateA = a.latestScrape ? new Date(a.latestScrape).getTime() : 0;
          const dateB = b.latestScrape ? new Date(b.latestScrape).getTime() : 0;
          return dateB - dateA;
        }));
      }
      console.error('Error deleting data set:', error);
      toast.error(error.response?.data?.error || 'Failed to delete data set');
    }
  };

  // Load data on mount
  useEffect(() => {
    // Initial load only - no polling
    loadActiveSessions();
    loadAllSessions('all');
    loadAccounts();
    loadActiveCheckerAccounts();
    loadSourceAccounts();
    loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reload sessions when filter changes
  useEffect(() => {
    if (sessionFilter) {
      loadAllSessions(sessionFilter);
    }
  }, [sessionFilter, loadAllSessions]);

  useEffect(() => {
    scraperSessionsPollRef.current = { activeSessions, allSessions, sessionFilter };
  }, [activeSessions, allSessions, sessionFilter]);

  // Poll Mongo-backed session progress while tasks run (works when scraper uses a separate Socket.IO without Redis adapter).
  useEffect(() => {
    const tick = () => {
      if (typeof document !== 'undefined' && document.hidden) return;
      const { activeSessions: a, allSessions: u, sessionFilter: f } =
        scraperSessionsPollRef.current;
      const combined = [...a, ...u];
      const seen = new Set();
      const unique = combined.filter((s) => {
        if (!s?.sessionId || seen.has(s.sessionId)) return false;
        seen.add(s.sessionId);
        return true;
      });
      const hasInFlight = unique.some(
        (s) => s.status === 'running' || s.status === 'paused'
      );
      if (!hasInFlight) return;
      loadActiveSessions();
      loadAllSessions(f);
    };
    const id = setInterval(tick, SCRAPER_PROGRESS_POLL_MS);
    return () => clearInterval(id);
  }, [loadActiveSessions, loadAllSessions]);

  // Load data sets when Data Sets tab is active
  useEffect(() => {
    if (activeTab === 'datasets') {
      loadDataSets();
    }
    return () => dataSetsStatsAbortRef.current?.abort();
  }, [activeTab, loadDataSets]);

  useEffect(() => {
    setScrapeSourcesPage(1);
  }, [scrapeSourcesTagFilter]);

  useEffect(() => {
    setSelectedScrapeSourceKeys([]);
  }, [scrapeSourcesPage, scrapeSourcesTagFilter]);

  useEffect(() => {
    const el = scrapeSourcesSelectAllRef.current;
    if (!el) return;
    const pageKeys = scrapeSourcesList.map((row) =>
      String(row.sourceUsername || '').toLowerCase().trim()
    );
    const n = pageKeys.filter((k) => selectedScrapeSourceKeys.includes(k)).length;
    const len = pageKeys.length;
    el.indeterminate = len > 0 && n > 0 && n < len;
  }, [scrapeSourcesList, selectedScrapeSourceKeys]);

  useEffect(() => {
    if (activeTab === 'followers') {
      loadScrapeSources();
    }
  }, [activeTab, loadScrapeSources]);

  // Validate usernames in real-time
  useEffect(() => {
    if (!debouncedUsernames || debouncedUsernames.trim().length === 0) {
      setUsernameValidation({ checking: false, errors: [], warnings: [], currentlyScraping: [] });
      return;
    }

    const checkUsernames = async () => {
      setUsernameValidation(prev => ({ ...prev, checking: true }));
      
      try {
        // Parse usernames from textarea (one per line)
        const usernameLines = debouncedUsernames
          .split('\n')
          .map(line => line.trim())
          .filter(line => line.length > 0);

        if (usernameLines.length === 0) {
          setUsernameValidation({ checking: false, errors: [], warnings: [], currentlyScraping: [] });
          return;
        }

        const response = await api.post('/scraper/check-duplicates', {
          usernames: usernameLines
        });

        const { currentlyScrapingUsernames = [], duplicateUsernames = [] } = response.data;
        
        setUsernameValidation({
          checking: false,
          errors: currentlyScrapingUsernames.map(u => ({
            username: u,
            message: `Currently being scraped in an active session`
          })),
          warnings: duplicateUsernames.map(d => ({
            username: d.username,
            message: `Already scraped ${d.scrapeCount} time(s) before`
          })),
          currentlyScraping: currentlyScrapingUsernames
        });
      } catch (error) {
        console.error('Error checking usernames:', error);
        setUsernameValidation({ checking: false, errors: [], warnings: [], currentlyScraping: [] });
      }
    };

    checkUsernames();
  }, [debouncedUsernames]);

  // Listen for scraper progress updates
  useEffect(() => {
    if (!socket) return;

    const handleProgress = (data) => {
      const updateSession = (session) => {
        if (session.sessionId === data.sessionId) {
          // Handle totalFollowers calculation update (partial update)
          if (data.updateType === 'totalFollowersCalculated') {
            return reconcileScraperSessionProgress({
              ...session,
              progress: {
                ...session.progress,
                totalFollowers: data.totalFollowers,
                // CRITICAL: Merge usernameStatus instead of replacing it
                usernameStatus: data.usernameStatus !== undefined 
                  ? { ...(session.progress.usernameStatus || {}), ...data.usernameStatus }
                  : (session.progress.usernameStatus || {})
              },
              results: data.results !== undefined ? data.results : (session.results || [])
            });
          }
          
          // Handle full progress update
          return reconcileScraperSessionProgress({
            ...session,
            // Preserve accountsInCooldown if not provided in progress update
            accountsInCooldown: data.accountsInCooldown !== undefined ? data.accountsInCooldown : (session.accountsInCooldown || 0),
            progress: {
              ...session.progress,
              completedUsernames: data.current !== undefined ? data.current : session.progress.completedUsernames,
              totalScraped: data.scraped !== undefined ? data.scraped : session.progress.totalScraped,
              totalStored: data.stored !== undefined ? data.stored : session.progress.totalStored,
              totalSkipped: data.skipped !== undefined ? data.skipped : session.progress.totalSkipped,
              totalDuplicates: data.duplicates !== undefined ? data.duplicates : (session.progress.totalDuplicates || 0),
              totalFollowers: data.totalFollowers !== undefined ? data.totalFollowers : (session.progress.totalFollowers || 0),
              totalBlacklisted: data.totalBlacklisted !== undefined ? data.totalBlacklisted : (session.progress.totalBlacklisted || 0),
              blacklistedUsernames: data.blacklistedUsernames !== undefined ? data.blacklistedUsernames : (session.progress.blacklistedUsernames || []),
              // Activity checking stats
              totalActivityChecked: data.totalActivityChecked !== undefined ? data.totalActivityChecked : (session.progress.totalActivityChecked || 0),
              totalActive: data.totalActive !== undefined ? data.totalActive : (session.progress.totalActive || 0),
              totalInactive: data.totalInactive !== undefined ? data.totalInactive : (session.progress.totalInactive || 0),
              totalFailed: data.totalFailed !== undefined ? data.totalFailed : (session.progress.totalFailed || 0),
              totalCountryChecked: data.totalCountryChecked !== undefined ? data.totalCountryChecked : (session.progress.totalCountryChecked || 0),
              totalCountryAccepted: data.totalCountryAccepted !== undefined ? data.totalCountryAccepted : (session.progress.totalCountryAccepted || 0),
              totalCountryRejected: data.totalCountryRejected !== undefined ? data.totalCountryRejected : (session.progress.totalCountryRejected || 0),
              totalCountryFailed: data.totalCountryFailed !== undefined ? data.totalCountryFailed : (session.progress.totalCountryFailed || 0),
              currentUsername: data.currentUsername !== undefined ? data.currentUsername : session.progress.currentUsername,
              currentUsernameFollowersCount: data.currentUsernameFollowersCount !== undefined ? data.currentUsernameFollowersCount : (session.progress.currentUsernameFollowersCount || 0),
              currentUsernameScraped: data.currentUsernameScraped !== undefined ? data.currentUsernameScraped : (session.progress.currentUsernameScraped || 0),
              withAvatar: data.withAvatar !== undefined ? data.withAvatar : (session.progress.withAvatar || 0),
              verified: data.verified !== undefined ? data.verified : (session.progress.verified || 0),
              canDm: data.canDm !== undefined ? data.canDm : (session.progress.canDm || 0),
              // CRITICAL: Merge usernameStatus instead of replacing it
              // This ensures progress for all usernames is preserved when multiple accounts scrape in parallel
              usernameStatus: data.usernameStatus !== undefined 
                ? { ...(session.progress.usernameStatus || {}), ...data.usernameStatus }
                : (session.progress.usernameStatus || {})
            },
            results: data.results !== undefined ? data.results : (session.results || []),
            status: data.status === 'completed' || data.status === 'stopped' || data.status === 'paused' ? data.status : session.status
          });
        }
        return session;
      };

      setActiveSessions(prev => prev.map(updateSession));
      setAllSessions(prev => prev.map(updateSession));

      if (data.status === 'completed') {
        toast.success(`Scraping completed: ${data.scraped} followers scraped, ${data.stored} stored`);
        loadActiveSessions();
        loadAllSessions(sessionFilter);
        loadStats();
      } else if (data.status === 'stopped') {
        toast.success(`Scraping stopped: ${data.scraped} followers scraped, ${data.stored} stored`);
        loadActiveSessions();
        loadAllSessions(sessionFilter);
        loadStats();
      } else if (data.status === 'paused') {
        toast.info('Scraping paused');
        loadActiveSessions();
        loadAllSessions(sessionFilter);
      }
    };

    const handleRateLimit = (data) => {
      setActiveSessions(prev => prev.map(session => {
        if (session.sessionId === data.sessionId) {
          // Always update accountsInCooldown if provided, otherwise keep current value
          const currentCooldown = session.accountsInCooldown || 0;
          const newCooldownCount = data.accountsInCooldown !== undefined 
            ? data.accountsInCooldown 
            : currentCooldown;
          
          // Only clear rateLimitInfo if cooldown has ended AND count is 0
          const shouldClearRateLimitInfo = data.cooldownEnded && newCooldownCount === 0;
          
          return {
            ...session,
            accountsInCooldown: newCooldownCount,
            rateLimitInfo: shouldClearRateLimitInfo ? null : (
              data.resetTimestamp ? {
                accountUsername: data.accountUsername,
                waitTime: data.waitTime,
                resetTimestamp: data.resetTimestamp,
                remainingSeconds: data.remainingSeconds
              } : session.rateLimitInfo
            )
          };
        }
        return session;
      }));
    };

    const handleActiveCheckerStatus = (data) => {
      const updateSession = (session) => {
        if (session.sessionId === data.sessionId) {
          return {
            ...session,
            activeCheckerStatus: {
              total: data.total,
              available: data.available,
              rateLimited: data.rateLimited
            }
          };
        }
        return session;
      };
      
      // Update both activeSessions and allSessions (UI renders from allSessions)
      setActiveSessions(prev => prev.map(updateSession));
      setAllSessions(prev => prev.map(updateSession));
    };

    const handleProgressBatch = (updates) => {
      if (!Array.isArray(updates) || updates.length === 0) return;
      setActiveSessions((prev) => applyScraperProgressBatchToSessions(prev, updates));
      setAllSessions((prev) => applyScraperProgressBatchToSessions(prev, updates));
    };

    const handleCountryQueueStats = (data) => {
      if (!data?.sessionId) return;
      const merge = (session) => {
        if (session.sessionId !== data.sessionId) return session;
        return {
          ...session,
          countryQueueStats: {
            waiting: data.waiting ?? 0,
            active: data.active ?? 0,
            completed: data.completed ?? 0,
            failed: data.failed ?? 0,
            skippedByCache: data.skippedByCache ?? 0
          }
        };
      };
      setActiveSessions((prev) => prev.map(merge));
      setAllSessions((prev) => prev.map(merge));
    };

    const handleActivityQueueStats = (data) => {
      if (!data?.sessionId) return;
      const merge = (session) => {
        if (session.sessionId !== data.sessionId) return session;
        return {
          ...session,
          activityQueueStats: {
            waiting: data.waiting ?? 0,
            active: data.active ?? 0,
            completed: data.completed ?? 0,
            failed: data.failed ?? 0
          }
        };
      };
      setActiveSessions((prev) => prev.map(merge));
      setAllSessions((prev) => prev.map(merge));
    };

    const handleSourceStatus = (data) => {
      const updateSession = (session) => {
        if (session.sessionId !== data.sessionId) return session;
        return {
          ...session,
          sourcing: data.sourcing || session.sourcing,
          sourceLoopRunning: data.status === 'running' || data.status === 'waiting'
        };
      };
      setActiveSessions(prev => prev.map(updateSession));
      setAllSessions(prev => prev.map(updateSession));
    };

    const handleSourcingDecisionsBatch = (data) => {
      if (!data?.sessionId || !data.decisions?.length) return;
      setSourcingDecisionsBySession((prev) => {
        const existing = prev[data.sessionId] || [];
        const merged = [...data.decisions, ...existing];
        return { ...prev, [data.sessionId]: merged.slice(0, 250) };
      });
    };

    // Phase 5: aggregated pipeline health snapshot (queues, CycleTLS pool,
    // checker claims, per-session funnels). Emitted every 3 s by EACH
    // replica, so we merge across replicas: queue/CycleTLS/session fields
    // come from the latest tick, but checker claims are kept in a
    // per-replica map below.
    const REPLICA_CLAIM_TTL_MS = 15000;
    const handlePipelineHealth = (data) => {
      if (!data) return;
      const ts = data.ts || Date.now();
      setPipelineHealth(data);

      // Track per-replica claim counts so the UI can show all 3 Railway
      // replicas side-by-side instead of flickering between them.
      const claimInstanceId = data.claims?.instanceId;
      if (claimInstanceId) {
        setReplicaClaims((prev) => {
          const next = { ...prev };
          next[claimInstanceId] = {
            instanceId: claimInstanceId,
            replicaLabel: data.claims?.replicaLabel || claimInstanceId.slice(0, 8),
            // Under the store-first pipeline, `accounts` is the shared
            // claimed-account pool for this replica (same pool used for
            // both country and activity coroutines); `country` / `activity`
            // become PROCESSED counters rather than claim counts.
            accounts: Number.isFinite(data.claims?.accounts) ? data.claims.accounts : null,
            country: data.claims?.country || 0,
            activity: data.claims?.activity || 0,
            ts,
          };
          // Prune stale replicas that haven't pinged in REPLICA_CLAIM_TTL_MS
          const cutoff = Date.now() - REPLICA_CLAIM_TTL_MS;
          for (const id of Object.keys(next)) {
            if (next[id].ts < cutoff) delete next[id];
          }
          return next;
        });
      }

      // Keep a small rolling window of per-session funnel samples so the
      // ThroughputEstimator can compute users/min deltas client-side without
      // any extra backend state.
      if (Array.isArray(data.sessions) && data.sessions.length > 0) {
        setPipelineHistory((prev) => {
          const next = [...prev];
          for (const s of data.sessions) {
            next.push({
              sessionId: s.sessionId,
              ts,
              totalScraped: s.funnel?.totalScraped || 0,
              totalCountryChecked: s.funnel?.totalCountryChecked || 0,
              totalActivityChecked: s.funnel?.totalActivityChecked || 0,
            });
          }
          return next.length > 200 ? next.slice(-200) : next;
        });
      }
    };

    socket.on('scraper-progress', handleProgress);
    socket.on('scraper-progress-batch', handleProgressBatch);
    socket.on('country-check-queue-stats', handleCountryQueueStats);
    socket.on('activity-check-queue-stats', handleActivityQueueStats);
    socket.on('scraper-rate-limit', handleRateLimit);
    socket.on('active-checker-status', handleActiveCheckerStatus);
    socket.on('source-status', handleSourceStatus);
    socket.on('sourcing-decisions-batch', handleSourcingDecisionsBatch);
    socket.on('scraper-pipeline-health', handlePipelineHealth);

    return () => {
      socket.off('scraper-progress', handleProgress);
      socket.off('scraper-progress-batch', handleProgressBatch);
      socket.off('country-check-queue-stats', handleCountryQueueStats);
      socket.off('activity-check-queue-stats', handleActivityQueueStats);
      socket.off('scraper-rate-limit', handleRateLimit);
      socket.off('active-checker-status', handleActiveCheckerStatus);
      socket.off('source-status', handleSourceStatus);
      socket.off('sourcing-decisions-batch', handleSourcingDecisionsBatch);
      socket.off('scraper-pipeline-health', handlePipelineHealth);
    };
  }, [socket, loadAllSessions, sessionFilter, loadActiveSessions, loadStats]);

  const handleStartScraping = async () => {
    if (!usernames.trim() && !sourcingEnabled) {
      toast.error('Please enter at least one username');
      return;
    }

    if (!taskName.trim()) {
      toast.error('Please enter a task name');
      return;
    }

    if (!tag.trim()) {
      toast.error('Please enter a tag');
      return;
    }

    if (selectedAccountIds.length === 0) {
      toast.error('Please select at least one account');
      return;
    }

    if ((filters.filterActiveLastMonth || filters.filterCountryWhitelist) && selectedActiveCheckerAccountIds.length === 0) {
      toast.error('Please select at least one active checker account when using activity or country whitelist filters');
      return;
    }

    if (filters.filterCountryWhitelist && allowedCountriesList.length === 0) {
      toast.error('Add at least one allowed country for the country whitelist');
      return;
    }

    const countNum = parseInt(count, 10);
    if (!Number.isFinite(countNum) || countNum < 1 || countNum > 200) {
      toast.error('Count per request must be between 1 and 200');
      return;
    }

    // Parse usernames (one per line) — may be empty when sourcing is enabled
    const usernameList = usernames
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0);

    if (usernameList.length === 0 && !sourcingEnabled) {
      toast.error('Please enter at least one valid username');
      return;
    }

    try {
      const response = await api.post('/scraper/start', {
        usernames: usernameList,
        count: countNum,
        filters: {
          ...filters,
          allowedCountries: filters.filterCountryWhitelist ? allowedCountriesList : []
        },
        tag: tag.trim(),
        name: taskName.trim(),
        accountIds: selectedAccountIds,
        activeCheckerAccountIds: (filters.filterActiveLastMonth || filters.filterCountryWhitelist) ? selectedActiveCheckerAccountIds : [],
        useBlacklist: useBlacklist,
        sourceAccountIds: sourcingEnabled ? selectedSourceAccountIds : [],
        sourcing: sourcingEnabled ? { enabled: true, ...sourcingConfig } : { enabled: false }
      }, { timeout: 60000 });

      if (response.data.success) {
        toast.success('Scraping session started');
        setShowStartModal(false);
        
        // Immediately add the new session to the UI (don't wait for reload)
        const newSession = response.data.session;
        if (newSession) {
          // Add to active sessions
          setActiveSessions(prev => [newSession, ...prev]);
          // Add to all sessions
          setAllSessions(prev => [newSession, ...prev]);
        }
        
        // Reset form
        setUsernames('');
        setTaskName(() => {
          // Reset to today's date
          const today = new Date();
          const day = String(today.getDate()).padStart(2, '0');
          const month = String(today.getMonth() + 1).padStart(2, '0');
          const year = today.getFullYear();
          return `${day}-${month}-${year}`;
        });
        setTag('');
        setCount(100);
        setSelectedAccountIds([]);
        setSelectedActiveCheckerAccountIds([]);
        setSelectedSourceAccountIds([]);
        setSourcingEnabled(false);
        setSourcingConfig({ grokFilter: true, containsLinks: false, bioWhitelist: false, minFollowers: 5000, maxFollowers: 500000, maxPendingTargets: 20 });
        setFilters({ filterAvatar: false, filterDMs: false, filterVerified: false, filterActiveLastMonth: false, filterCountryWhitelist: false });
        setAllowedCountriesList([...DEFAULT_SCRAPER_COUNTRY_WHITELIST]);
        setCountryAddInput('');
        setUseBlacklist(false);
        
        // Reload accounts to update "In Use" status
        loadAccounts();
        loadActiveCheckerAccounts();
        loadSourceAccounts();
        loadStats();
      } else {
        const errorMsg = response.data.error || 'Failed to start scraping';
        if (response.data.invalidAccounts && response.data.invalidAccounts.length > 0) {
          const accountList = response.data.invalidAccounts.map(acc => acc.username).join(', ');
          toast.error(`${errorMsg}\nAccounts: ${accountList}`, { duration: 8000 });
        } else {
          toast.error(errorMsg);
        }
      }
    } catch (error) {
      console.error('Error starting scraper:', error);
      const errorData = error.response?.data;
      if (errorData?.invalidAccounts && errorData.invalidAccounts.length > 0) {
        const accountList = errorData.invalidAccounts.map(acc => acc.username).join(', ');
        toast.error(`${errorData.error || 'Failed to start scraping'}\nAccounts: ${accountList}`, { duration: 8000 });
      } else {
        toast.error(errorData?.error || 'Failed to start scraping');
      }
    }
  };

  const handleStopSession = async (sessionId) => {
    try {
      const response = await api.post(`/scraper/sessions/${sessionId}/stop`);
      if (response.data.success) {
        toast.success('Session stopped');
        loadActiveSessions();
        loadAllSessions(sessionFilter);
      } else {
        toast.error(response.data.error || 'Failed to stop session');
      }
    } catch (error) {
      console.error('Error stopping session:', error);
      toast.error(error.response?.data?.error || 'Failed to stop session');
    }
  };

  const handlePauseSession = async (sessionId) => {
    try {
      const response = await api.post(`/scraper/sessions/${sessionId}/pause`);
      if (response.data.success) {
        toast.success('Session paused');
        loadActiveSessions();
        loadAllSessions(sessionFilter);
      } else {
        toast.error(response.data.error || 'Failed to pause session');
      }
    } catch (error) {
      console.error('Error pausing session:', error);
      toast.error(error.response?.data?.error || 'Failed to pause session');
    }
  };

  const handleResumeSession = async (sessionId) => {
    try {
      const response = await api.post(`/scraper/sessions/${sessionId}/resume`);
      if (response.data.success) {
        toast.success('Session resumed');
        loadActiveSessions();
        loadAllSessions(sessionFilter);
      } else {
        toast.error(response.data.error || 'Failed to resume session');
      }
    } catch (error) {
      console.error('Error resuming session:', error);
      toast.error(error.response?.data?.error || 'Failed to resume session');
    }
  };

  const handleRestartSession = async (sessionId) => {
    try {
      const response = await api.post(`/scraper/sessions/${sessionId}/restart`);
      if (response.data.success) {
        toast.success(`Session restarted (${response.data.remainingUsernames} usernames remaining)`);
        loadActiveSessions();
        loadAllSessions(sessionFilter);
      } else {
        toast.error(response.data.error || 'Failed to restart session');
      }
    } catch (error) {
      console.error('Error restarting session:', error);
      toast.error(error.response?.data?.error || 'Failed to restart session');
    }
  };

  const [followerPageCountDraft, setFollowerPageCountDraft] = useState({});
  const [followerPageCountSaving, setFollowerPageCountSaving] = useState({});

  const handleApplyFollowerPageCount = async (session) => {
    const sid = session.sessionId;
    const raw =
      followerPageCountDraft[sid] !== undefined
        ? followerPageCountDraft[sid]
        : String(session.count ?? 100);
    const n = parseInt(String(raw).trim(), 10);
    if (!Number.isFinite(n) || n < 1 || n > 200) {
      toast.error('Followers per page must be between 1 and 200');
      return;
    }
    setFollowerPageCountSaving((prev) => ({ ...prev, [sid]: true }));
    try {
      const response = await api.patch(
        `/scraper/sessions/${encodeURIComponent(sid)}/follower-page-count`,
        { count: n }
      );
      if (response.data?.success) {
        toast.success(`Followers per page set to ${n} (scrapers pick this up on the next page fetch)`);
        setFollowerPageCountDraft((prev) => {
          const next = { ...prev };
          delete next[sid];
          return next;
        });
        loadActiveSessions();
        loadAllSessions(sessionFilter);
      } else {
        toast.error(response.data?.error || 'Failed to update page size');
      }
    } catch (error) {
      console.error('Error updating follower page count:', error);
      toast.error(error.response?.data?.error || 'Failed to update page size');
    } finally {
      setFollowerPageCountSaving((prev) => ({ ...prev, [sid]: false }));
    }
  };

  const handleDeleteSession = async (sessionId, sessionLabel) => {
    if (
      !window.confirm(
        `Permanently delete session "${sessionLabel || sessionId}"? This removes the task from your history only — scraped follower data in Data Sets is not deleted.`
      )
    ) {
      return;
    }
    setDeletingSessionId(sessionId);
    try {
      const response = await api.delete(`/scraper/sessions/${encodeURIComponent(sessionId)}`);
      if (response.data.success) {
        toast.success('Session deleted');
        loadActiveSessions();
        loadAllSessions(sessionFilter);
      } else {
        toast.error(response.data.error || 'Failed to delete session');
      }
    } catch (error) {
      console.error('Error deleting session:', error);
      toast.error(error.response?.data?.error || error.response?.data?.message || 'Failed to delete session');
    } finally {
      setDeletingSessionId(null);
    }
  };

  const scrapeSourceRowKey = (row) => String(row.sourceUsername || '').toLowerCase().trim();

  /** Delete scraped follower rows for this source; respects tag filter when set (narrow delete). */
  const deleteScrapeSourceApi = async (sourceUsername) => {
    const src = String(sourceUsername || '').toLowerCase().trim();
    if (!src) throw new Error('Invalid source username');
    const body = { sourceUsername: src };
    const tagTrim = scrapeSourcesTagFilter.trim();
    if (tagTrim) {
      body.tag = tagTrim;
    }
    const response = await api.delete('/scraper/scrape-sources', { data: body });
    if (!response.data.success) {
      throw new Error(response.data.error || 'Delete failed');
    }
    return {
      deletedCount: response.data.deletedCount ?? 0,
      removedSourceUsername: !!response.data.removedSourceUsername
    };
  };

  /** All keys matching current Usernames tab filter (cross-page selection). */
  const fetchScrapeSourceKeysMatchingFilter = useCallback(async () => {
    const params = {};
    const tagTrim = scrapeSourcesTagFilter.trim();
    if (tagTrim) {
      params.tag = tagTrim;
    }
    const response = await api.get('/scraper/scrape-source-keys', { params });
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to load keys');
    }
    return response.data.usernames || [];
  }, [scrapeSourcesTagFilter]);

  const handleDeleteScrapeSourceRow = async (row) => {
    const src = scrapeSourceRowKey(row);
    if (!src) return;
    const tagTrim = scrapeSourcesTagFilter.trim();
    const scope =
      tagTrim.length > 0
        ? `under tag "${tagTrim === '(no tag)' ? '(no tag)' : tagTrim}" only`
        : 'across all tags';
    if (
      !window.confirm(
        `Remove all scraped followers taken from @${src} ${scope}? (${(row.followerCount ?? 0).toLocaleString()} row(s) on this view.) This cannot be undone.`
      )
    ) {
      return;
    }
    setDeletingScrapeSourceId(src);
    try {
      const { deletedCount, removedSourceUsername } = await deleteScrapeSourceApi(src);
      if (deletedCount > 0) {
        toast.success(`Removed ${deletedCount.toLocaleString()} scraped follower row(s)`);
      } else if (removedSourceUsername) {
        toast.success('Removed target from history (no follower rows were stored)');
      } else {
        toast.success('Nothing to remove');
      }
      setSelectedScrapeSourceKeys((prev) => prev.filter((k) => k !== src));
      loadStats();
      if (activeTab === 'followers') {
        loadScrapeSources();
      }
    } catch (error) {
      console.error('Error deleting scrape source:', error);
      toast.error(error.response?.data?.error || error.message || 'Delete failed');
    } finally {
      setDeletingScrapeSourceId(null);
    }
  };

  const toggleScrapeSourceKeySelected = (key) => {
    setSelectedScrapeSourceKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const toggleSelectAllScrapeSourcesOnPage = () => {
    const pageKeys = scrapeSourcesList.map(scrapeSourceRowKey).filter(Boolean);
    const allOn =
      pageKeys.length > 0 && pageKeys.every((k) => selectedScrapeSourceKeys.includes(k));
    if (allOn) {
      setSelectedScrapeSourceKeys((prev) => prev.filter((k) => !pageKeys.includes(k)));
    } else {
      setSelectedScrapeSourceKeys((prev) => [...new Set([...prev, ...pageKeys])]);
    }
  };

  const handleSelectAllScrapeSourcesMatchingFilter = async () => {
    const total = scrapeSourcesPagination.total ?? 0;
    if (total <= 0) {
      toast.error('Nothing to select');
      return;
    }
    setScrapeSourcesSelectAllMatchingLoading(true);
    try {
      const keys = await fetchScrapeSourceKeysMatchingFilter();
      const uniq = [...new Set(keys.map((k) => String(k || '').toLowerCase().trim()).filter(Boolean))];
      setSelectedScrapeSourceKeys(uniq);
      toast.success(`Selected ${uniq.length.toLocaleString()} target(s) matching this filter`);
    } catch (e) {
      console.error('Select all scrape sources failed:', e);
      toast.error(e.response?.data?.error || e.message || 'Failed to select all');
    } finally {
      setScrapeSourcesSelectAllMatchingLoading(false);
    }
  };

  const handleBulkDeleteScrapeSources = async () => {
    const keysToDelete = selectedScrapeSourceKeys.filter(Boolean);
    if (keysToDelete.length === 0) {
      toast.error('Select at least one row');
      return;
    }
    const tagTrim = scrapeSourcesTagFilter.trim();
    const scope = tagTrim.length > 0 ? ` (only under the current tag filter)` : ' (all tags)';
    if (
      !window.confirm(
        `Permanently remove data for ${keysToDelete.length.toLocaleString()} target(s)${scope}? Follower rows and/or history entries will be removed. This cannot be undone.`
      )
    ) {
      return;
    }
    setBulkDeletingScrapeSources(true);
    let ok = 0;
    let failed = 0;
    let followerRowsRemoved = 0;
    let historyRowsRemoved = 0;
    try {
      for (const src of keysToDelete) {
        try {
          const { deletedCount, removedSourceUsername } = await deleteScrapeSourceApi(src);
          ok += 1;
          followerRowsRemoved += deletedCount;
          if (removedSourceUsername) historyRowsRemoved += 1;
        } catch (e) {
          failed += 1;
          console.error('Bulk delete scrape source failed:', e);
        }
      }
      if (followerRowsRemoved > 0 || historyRowsRemoved > 0) {
        const parts = [];
        if (followerRowsRemoved > 0) {
          parts.push(`${followerRowsRemoved.toLocaleString()} follower row(s)`);
        }
        if (historyRowsRemoved > 0) {
          parts.push(`${historyRowsRemoved.toLocaleString()} history entr${historyRowsRemoved === 1 ? 'y' : 'ies'}`);
        }
        toast.success(
          `Removed ${parts.join(' and ')} across ${ok.toLocaleString()} target(s)${failed ? `, ${failed} failed` : ''}`
        );
      } else if (failed > 0) {
        toast.error(`Could not remove data (${failed} target(s) failed)`);
      } else {
        toast.error('No data was removed');
      }
      setSelectedScrapeSourceKeys([]);
      loadStats();
      loadScrapeSources();
    } finally {
      setBulkDeletingScrapeSources(false);
    }
  };

  const scrapeSourcesPageSelectKeys = scrapeSourcesList.map(scrapeSourceRowKey).filter(Boolean);
  const allScrapeSourcesPageSelected =
    scrapeSourcesPageSelectKeys.length > 0 &&
    scrapeSourcesPageSelectKeys.every((k) => selectedScrapeSourceKeys.includes(k));

  // ✅ Add usernames to a running session on-the-go
  const [addUsernamesInput, setAddUsernamesInput] = useState({});
  const [addingUsernames, setAddingUsernames] = useState({});

  // Extract clean username from URL or @username input
  const extractUsername = (input) => {
    let u = input.trim();
    // Remove @ prefix first
    u = u.replace(/^@/, '');
    // Remove protocol (http:// or https://)
    u = u.replace(/^https?:\/\//, '');
    // Remove x.com/ or twitter.com/ prefix
    u = u.replace(/^(www\.)?(x\.com|twitter\.com)\//, '');
    // Remove trailing slash
    u = u.replace(/\/$/, '');
    // Remove query params and fragments
    u = u.split('?')[0].split('#')[0];
    // Take only the first path segment (the username)
    u = u.split('/')[0];
    return u.toLowerCase().trim();
  };

  const handleAddUsernames = async (sessionId) => {
    const inputText = addUsernamesInput[sessionId] || '';
    const newUsernames = inputText
      .split(/[\n,]+/)
      .map(u => extractUsername(u))
      .filter(u => u.length > 0);
    
    if (newUsernames.length === 0) {
      toast.error('Please enter at least one username');
      return;
    }
    
    setAddingUsernames(prev => ({ ...prev, [sessionId]: true }));
    try {
      const response = await api.post(`/scraper/sessions/${sessionId}/add-usernames`, {
        usernames: newUsernames
      });
      if (response.data.success) {
        const msg = response.data.added > 0 
          ? `Added ${response.data.added} new username${response.data.added > 1 ? 's' : ''} to queue${response.data.duplicatesSkipped > 0 ? ` (${response.data.duplicatesSkipped} duplicates skipped)` : ''}`
          : 'All usernames already exist in this session';
        toast.success(msg);
        setAddUsernamesInput(prev => ({ ...prev, [sessionId]: '' }));
        loadActiveSessions();
      } else {
        toast.error(response.data.error || 'Failed to add usernames');
      }
    } catch (error) {
      console.error('Error adding usernames:', error);
      toast.error(error.response?.data?.error || 'Failed to add usernames');
    } finally {
      setAddingUsernames(prev => ({ ...prev, [sessionId]: false }));
    }
  };

  // Fix corrupted usernames in a session (e.g., @https://x.com/user → user)
  const [fixingUsernames, setFixingUsernames] = useState({});
  /** `${sessionId}|all` | `${sessionId}|new` while export request in flight */
  const [recipientExportKey, setRecipientExportKey] = useState(null);
  /** Live progress for chunked exports: { done, total } or null when idle. */
  const [recipientExportProgress, setRecipientExportProgress] = useState(null);

  /** Extract-New sizing modal — session being configured, or null. */
  const [extractNewModal, setExtractNewModal] = useState(null);
  /** Cached counts for the extract-new modal: { [sessionId]: { total, exported, available } }. */
  const [extractNewStats, setExtractNewStats] = useState({});
  const [extractNewStatsLoading, setExtractNewStatsLoading] = useState(false);

  /**
   * Export recipients for a session.
   *  - mode='all'  → dump everything (unchanged behaviour).
   *  - mode='new'  → only rows with no `bulkTextExportAt`, marking them as
   *    exported. Optional `accounts` + `follows` cap the row count at
   *    accounts * follows. Leaving both at 0 restores the uncapped legacy
   *    behaviour so existing direct links keep working.
   */
  const handleRecipientsExport = async (sessionId, mode, options = {}) => {
    const key = `${sessionId}|${mode}`;
    setRecipientExportKey(key);
    const exportOpts = { timeout: 0 };

    const readExportHeaders = (res) => {
      const exportCountHeader = res.headers['x-export-count'] ?? res.headers['X-Export-Count'];
      const exportedRows =
        exportCountHeader != null && exportCountHeader !== ''
          ? parseInt(exportCountHeader, 10)
          : null;
      const nextAfterRaw =
        res.headers['x-export-next-after-id'] ?? res.headers['X-Export-Next-After-Id'] ?? '';
      const nextAfter = String(nextAfterRaw || '').trim() || null;
      return { exportedRows, nextAfter };
    };

    const blobText = async (blob) => {
      if (blob != null && typeof blob.text === 'function') return blob.text();
      return String(blob ?? '');
    };

    const triggerDownload = (blob, filename) => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    };

    try {
      const path = `/scraper/sessions/${encodeURIComponent(sessionId)}/recipients-export`;

      if (mode === 'all') {
        let totalWritten = 0;
        let startAfter = null;
        const textParts = [];
        let filename = `recipients-${mode}.txt`;

        for (;;) {
          const params = { mode: 'all', maxRows: RECIPIENT_EXPORT_CHUNK_ROWS };
          if (startAfter) params.startAfterId = startAfter;
          const res = await api.get(path, { params, responseType: 'blob', ...exportOpts });
          const cd = res.headers['content-disposition'] ?? res.headers['Content-Disposition'];
          if (cd && !startAfter) {
            const m = /filename\*=UTF-8''([^;\n]+)|filename="([^"]+)"/i.exec(cd);
            const raw = (m && (m[1] || m[2]) ? decodeURIComponent(String(m[1] || m[2]).trim()) : '') || '';
            if (raw) filename = raw;
          }
          const { exportedRows, nextAfter } = readExportHeaders(res);
          const text = await blobText(res.data);
          if (text) textParts.push(text);
          if (exportedRows != null && Number.isFinite(exportedRows)) totalWritten += exportedRows;
          if (!nextAfter) break;
          startAfter = nextAfter;
        }

        triggerDownload(new Blob(textParts, { type: 'text/plain;charset=utf-8' }), filename);
        toast.success(
          totalWritten > 0
            ? `Downloaded ${totalWritten.toLocaleString()} stored recipients (this session's tag)`
            : "Downloaded all stored recipients for this session's tag"
        );
        return true;
      }

      const accounts = Math.max(0, parseInt(options.accounts, 10) || 0);
      const follows = Math.max(0, parseInt(options.follows, 10) || 0);
      const followsParam = follows > 0 ? follows : 48;
      const requested =
        accounts > 0 && follows > 0
          ? accounts * follows
          : follows > 0
            ? follows
            : accounts > 0
              ? accounts
              : 0;

      const chunkNewExport = async () => {
        let totalWritten = 0;
        const textParts = [];
        let filename = `recipients-${mode}.txt`;
        /** Lines actually exported so far (matches server marking). */
        let linesSoFar = 0;

        setRecipientExportProgress({ done: 0, total: requested });

        for (;;) {
          if (requested > 0 && linesSoFar >= requested) break;
          const stillNeed = requested > 0 ? requested - linesSoFar : RECIPIENT_EXPORT_CHUNK_ROWS;
          if (requested > 0 && stillNeed <= 0) break;
          const thisTarget = Math.min(RECIPIENT_EXPORT_CHUNK_ROWS, stillNeed);
          const params =
            stillNeed < followsParam
              ? { mode: 'new', accounts: 1, follows: stillNeed }
              : {
                  mode: 'new',
                  accounts: Math.max(1, Math.ceil(thisTarget / followsParam)),
                  follows: followsParam,
                };
          const res = await api.get(path, { params, responseType: 'blob', ...exportOpts });
          const cd = res.headers['content-disposition'] ?? res.headers['Content-Disposition'];
          if (cd && textParts.length === 0) {
            const m = /filename\*=UTF-8''([^;\n]+)|filename="([^"]+)"/i.exec(cd);
            const raw = (m && (m[1] || m[2]) ? decodeURIComponent(String(m[1] || m[2]).trim()) : '') || '';
            if (raw) filename = raw;
          }
          const { exportedRows } = readExportHeaders(res);
          const text = await blobText(res.data);
          if (text) textParts.push(text);
          const n = exportedRows != null && Number.isFinite(exportedRows) ? exportedRows : 0;
          totalWritten += n;
          linesSoFar += n;
          setRecipientExportProgress({ done: linesSoFar, total: requested });
          if (n === 0) break;
        }

        triggerDownload(new Blob(textParts, { type: 'text/plain;charset=utf-8' }), filename);
        if (totalWritten === 0) {
          toast('No new recipients available to export', { icon: 'ℹ️' });
        } else {
          toast.success(`Exported ${totalWritten.toLocaleString()} new recipients (marked as exported)`);
        }
        return true;
      };

      if (mode === 'new' && (requested === 0 || requested > RECIPIENT_EXPORT_CHUNK_ROWS)) {
        return await chunkNewExport();
      }

      const params = { mode };
      if (accounts > 0) params.accounts = accounts;
      if (follows > 0) params.follows = follows;
      const res = await api.get(path, { params, responseType: 'blob', ...exportOpts });
      let filename = `recipients-${mode}.txt`;
      const cd = res.headers['content-disposition'] ?? res.headers['Content-Disposition'];
      if (cd) {
        const m = /filename\*=UTF-8''([^;\n]+)|filename="([^"]+)"/i.exec(cd);
        const raw = (m && (m[1] || m[2]) ? decodeURIComponent(String(m[1] || m[2]).trim()) : '') || '';
        if (raw) filename = raw;
      }
      const { exportedRows } = readExportHeaders(res);
      const blob = new Blob([await blobText(res.data)], { type: 'text/plain;charset=utf-8' });
      triggerDownload(blob, filename);

      if (mode === 'new') {
        if (exportedRows != null && Number.isFinite(exportedRows)) {
          if (exportedRows === 0) {
            toast('No new recipients available to export', { icon: 'ℹ️' });
          } else {
            toast.success(`Exported ${exportedRows.toLocaleString()} new recipients (marked as exported)`);
          }
        } else {
          toast.success('Downloaded recipients not exported before (marked as exported)');
        }
      }
      return true;
    } catch (error) {
      console.error('Error exporting recipients:', error);
      const data = error.response?.data;
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          const j = JSON.parse(text);
          toast.error(j.error || j.message || 'Export failed');
        } catch {
          toast.error('Export failed');
        }
      } else {
        toast.error(error.response?.data?.error || error.message || 'Export failed');
      }
      return false;
    } finally {
      setRecipientExportKey(null);
      setRecipientExportProgress(null);
    }
  };

  /** Fetch how many rows are available / already exported for the modal. */
  const fetchExtractNewStats = useCallback(async (sessionId) => {
    setExtractNewStatsLoading(true);
    try {
      const res = await api.get(
        `/scraper/sessions/${encodeURIComponent(sessionId)}/recipients-export-stats`
      );
      if (res.data?.success) {
        setExtractNewStats(prev => ({
          ...prev,
          [sessionId]: {
            total: res.data.total || 0,
            exported: res.data.exported || 0,
            available: res.data.available || 0,
          },
        }));
      }
    } catch (err) {
      // Non-fatal: modal still works without stats.
    } finally {
      setExtractNewStatsLoading(false);
    }
  }, []);

  /** Open the Extract-New sizing modal and pre-fetch stats. */
  const openExtractNewModal = useCallback(
    (session) => {
      setExtractNewModal({
        sessionId: session.sessionId,
        name: session.name || session.tag || session.sessionId,
        accounts: 1000,
        follows: 48,
      });
      fetchExtractNewStats(session.sessionId);
    },
    [fetchExtractNewStats]
  );

  const handleFixUsernames = async (sessionId) => {
    setFixingUsernames(prev => ({ ...prev, [sessionId]: true }));
    try {
      const response = await api.post(`/scraper/sessions/${sessionId}/fix-usernames`);
      if (response.data.success) {
        if (response.data.fixed === 0 && response.data.removedDuplicates === 0) {
          toast.success('All usernames are already clean');
        } else {
          toast.success(`Fixed ${response.data.fixed} usernames, removed ${response.data.removedDuplicates} duplicates (${response.data.totalUsernames} total)`);
        }
        loadActiveSessions();
        loadAllSessions(sessionFilter);
      } else {
        toast.error(response.data.error || 'Failed to fix usernames');
      }
    } catch (error) {
      console.error('Error fixing usernames:', error);
      toast.error(error.response?.data?.error || 'Failed to fix usernames');
    } finally {
      setFixingUsernames(prev => ({ ...prev, [sessionId]: false }));
    }
  };

  const toggleAccountSelection = (accountId) => {
    // Prevent selecting accounts that are in use or don't have valid sessions
    const account = accounts.find(acc => acc._id === accountId);
    if (account && (account.inUse || !account.hasValidSession)) {
      return;
    }
    
    setSelectedAccountIds(prev => {
      if (prev.includes(accountId)) {
        return prev.filter(id => id !== accountId);
      } else {
        return [...prev, accountId];
      }
    });
  };

  const handleSelectAllAccounts = () => {
    // Only select accounts that are not in use and have valid sessions
    const selectableAccountIds = accounts
      .filter(acc => acc.hasValidSession && !acc.inUse)
      .map(acc => acc._id);
    
    const allSelected = selectableAccountIds.length > 0 && 
      selectableAccountIds.every(id => selectedAccountIds.includes(id));
    
    if (allSelected) {
      // Deselect all
      setSelectedAccountIds([]);
    } else {
      // Select all selectable accounts
      setSelectedAccountIds(selectableAccountIds);
    }
  };

  const handleToggleGroup = (groupName) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupName)) {
        next.delete(groupName);
      } else {
        next.add(groupName);
      }
      return next;
    });
  };

  const handleSelectGroup = (groupName) => {
    const groupAccounts = accounts.filter(acc => {
      if (!acc.group || acc.group.trim() !== groupName.trim()) return false;
      return acc.hasValidSession === true && acc.inUse !== true;
    });
    
    if (groupAccounts.length === 0) return;
    
    const groupAccountIds = groupAccounts.map(acc => acc._id);
    const allGroupSelected = groupAccountIds.every(id => selectedAccountIds.includes(id));
    
    if (allGroupSelected) {
      setSelectedAccountIds(prev => prev.filter(id => !groupAccountIds.includes(id)));
    } else {
      setSelectedAccountIds(prev => [...new Set([...prev, ...groupAccountIds])]);
    }
  };

  // Active Checker Account handlers
  const toggleActiveCheckerAccountSelection = (accountId) => {
    setSelectedActiveCheckerAccountIds(prev => {
      if (prev.includes(accountId)) {
        return prev.filter(id => id !== accountId);
      } else {
        return [...prev, accountId];
      }
    });
  };

  const handleSelectAllActiveCheckerAccounts = () => {
    const allSelected = activeCheckerAccounts.length > 0 && 
      activeCheckerAccounts.every(acc => selectedActiveCheckerAccountIds.includes(acc._id));
    
    if (allSelected) {
      // Deselect all
      setSelectedActiveCheckerAccountIds([]);
    } else {
      // Select all active checker accounts
      setSelectedActiveCheckerAccountIds(activeCheckerAccounts.map(acc => acc._id));
    }
  };

  const handleToggleActiveCheckerGroup = (groupName) => {
    setExpandedActiveCheckerGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupName)) {
        next.delete(groupName);
      } else {
        next.add(groupName);
      }
      return next;
    });
  };

  const handleSelectActiveCheckerGroup = (groupName) => {
    const groupAccounts = activeCheckerAccounts.filter(acc => {
      if (!acc.group || acc.group.trim() !== groupName.trim()) return false;
      return true; // All active checker accounts are selectable
    });
    
    if (groupAccounts.length === 0) return;
    
    const groupAccountIds = groupAccounts.map(acc => acc._id);
    const allGroupSelected = groupAccountIds.every(id => selectedActiveCheckerAccountIds.includes(id));
    
    if (allGroupSelected) {
      setSelectedActiveCheckerAccountIds(prev => prev.filter(id => !groupAccountIds.includes(id)));
    } else {
      setSelectedActiveCheckerAccountIds(prev => [...new Set([...prev, ...groupAccountIds])]);
    }
  };

  // Source Account handlers
  const toggleSourceAccountSelection = (accountId) => {
    setSelectedSourceAccountIds(prev => {
      if (prev.includes(accountId)) {
        return prev.filter(id => id !== accountId);
      } else {
        return [...prev, accountId];
      }
    });
  };

  const handleSelectAllSourceAccounts = () => {
    const selectableIds = sourceAccounts.filter(acc => acc.hasValidSession && !acc.inUse).map(acc => acc._id);
    const allSelected = selectableIds.length > 0 && selectableIds.every(id => selectedSourceAccountIds.includes(id));
    if (allSelected) {
      setSelectedSourceAccountIds([]);
    } else {
      setSelectedSourceAccountIds(selectableIds);
    }
  };

  const handleToggleSourceGroup = (groupName) => {
    setExpandedSourceGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupName)) {
        next.delete(groupName);
      } else {
        next.add(groupName);
      }
      return next;
    });
  };

  const handleSelectSourceGroup = (groupName) => {
    const groupAccounts = sourceAccounts.filter(acc => {
      if (!acc.group || acc.group.trim() !== groupName.trim()) return false;
      return acc.hasValidSession === true && acc.inUse !== true;
    });
    if (groupAccounts.length === 0) return;
    const groupAccountIds = groupAccounts.map(acc => acc._id);
    const allGroupSelected = groupAccountIds.every(id => selectedSourceAccountIds.includes(id));
    if (allGroupSelected) {
      setSelectedSourceAccountIds(prev => prev.filter(id => !groupAccountIds.includes(id)));
    } else {
      setSelectedSourceAccountIds(prev => [...new Set([...prev, ...groupAccountIds])]);
    }
  };

  const handleToggleUsernameDetails = (sessionId) => {
    setExpandedUsernameDetails(prev => {
      const next = new Set(prev);
      if (next.has(sessionId)) {
        next.delete(sessionId);
      } else {
        next.add(sessionId);
      }
      return next;
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <ArrowPathIcon className="animate-spin h-8 w-8 text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Hero Banner */}
      <div className={`relative overflow-hidden rounded-2xl ${
        isDarkMode 
          ? 'bg-gradient-to-r from-slate-900 via-blue-950 to-slate-900' 
          : 'bg-gradient-to-r from-sky-50 via-blue-100 to-sky-100'
      }`}>
        {/* Decorative elements */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Gradient orbs */}
          <div className={`absolute -top-16 left-1/4 w-80 h-80 rounded-full blur-3xl ${
            isDarkMode ? 'bg-blue-600/20' : 'bg-blue-300/40'
          }`} />
          <div className={`absolute -bottom-20 right-1/4 w-72 h-72 rounded-full blur-3xl ${
            isDarkMode ? 'bg-sky-600/15' : 'bg-sky-300/35'
          }`} />
          <div className={`absolute top-1/2 -right-16 w-56 h-56 rounded-full blur-3xl ${
            isDarkMode ? 'bg-cyan-600/10' : 'bg-cyan-200/30'
          }`} />
          
          {/* Grid pattern overlay */}
          <div 
            className={`absolute inset-0 ${isDarkMode ? 'opacity-[0.03]' : 'opacity-[0.08]'}`}
            style={{
              backgroundImage: `linear-gradient(${isDarkMode ? '#fff' : '#3b82f6'} 1px, transparent 1px), linear-gradient(90deg, ${isDarkMode ? '#fff' : '#3b82f6'} 1px, transparent 1px)`,
              backgroundSize: '40px 40px'
            }}
          />
          
          {/* Floating decorative shapes */}
          <svg className="absolute top-10 right-20 w-24 h-24 opacity-10" viewBox="0 0 100 100">
            <circle cx="30" cy="30" r="20" fill="none" stroke={isDarkMode ? '#93c5fd' : '#3b82f6'} strokeWidth="1.5" />
            <circle cx="70" cy="50" r="15" fill="none" stroke={isDarkMode ? '#93c5fd' : '#3b82f6'} strokeWidth="1.5" />
            <circle cx="40" cy="70" r="18" fill="none" stroke={isDarkMode ? '#93c5fd' : '#3b82f6'} strokeWidth="1.5" />
          </svg>
          <svg className="absolute bottom-8 left-16 w-16 h-16 opacity-10" viewBox="0 0 100 100">
            <polygon points="50,10 90,40 75,90 25,90 10,40" fill="none" stroke={isDarkMode ? '#7dd3fc' : '#0284c7'} strokeWidth="2" />
          </svg>
        </div>
        
        {/* Content */}
        <div className="relative px-8 py-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              {/* Icon/Logo area */}
              <div className={`relative flex items-center justify-center w-16 h-16 rounded-2xl ${
                isDarkMode 
                  ? 'bg-gradient-to-br from-blue-500 to-sky-600 shadow-lg shadow-blue-500/30' 
                  : 'bg-gradient-to-br from-blue-500 to-sky-500 shadow-lg shadow-blue-400/40'
              }`}>
                <UserGroupIcon className="w-8 h-8 text-white" />
                <div className={`absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center ${
                  isDarkMode ? 'bg-cyan-400' : 'bg-cyan-500'
                }`}>
                  <SparklesIcon className="w-3 h-3 text-white" />
                </div>
              </div>
              
              {/* Text content */}
              <div>
                <h1 className={`text-3xl font-bold tracking-tight ${
                  isDarkMode ? 'text-white' : 'text-slate-900'
                }`}>
                  Follower Scraper
                </h1>
                <p className={`mt-1.5 text-base ${
                  isDarkMode ? 'text-slate-300' : 'text-slate-600'
                }`}>
                  Scrape followers from X.com users and store them in the database
                </p>
                {stats && (
                  <div className={`mt-3 flex items-center space-x-4 text-sm ${
                    isDarkMode ? 'text-slate-400' : 'text-slate-500'
                  }`}>
                    <span className="flex items-center">
                      <span className={`w-2 h-2 rounded-full mr-2 ${
                        isDarkMode ? 'bg-emerald-400' : 'bg-emerald-500'
                      }`} />
                      {stats.total.toLocaleString()} Scraped
                    </span>
                    <span className="flex items-center">
                      <span className={`w-2 h-2 rounded-full mr-2 ${
                        isDarkMode ? 'bg-blue-400' : 'bg-blue-500'
                      }`} />
                      {activeSessions.filter(s => s.status === 'running').length} Active
                      {activeSessions.filter(s => s.status === 'paused').length > 0 && (
                        <span className="ml-2 text-yellow-600 dark:text-yellow-400">
                          {activeSessions.filter(s => s.status === 'paused').length} Paused
                        </span>
                      )}
                    </span>
                  </div>
                )}
              </div>
            </div>
            
            {/* Action buttons */}
            <div className="flex items-center space-x-3">
              <button
                onClick={() => {
                  if (user?.role !== 'admin' && !user?.features?.canUseScraper) {
                    setShowPermissionModal(true);
                    return;
                  }
                  setShowStartModal(true);
                }}
                className={`inline-flex items-center px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${
                  isDarkMode 
                    ? 'bg-gradient-to-r from-blue-500 to-sky-500 hover:from-blue-400 hover:to-sky-400 text-white shadow-lg shadow-blue-500/30 hover:shadow-blue-500/40' 
                    : 'bg-gradient-to-r from-blue-600 to-sky-600 hover:from-blue-500 hover:to-sky-500 text-white shadow-lg shadow-blue-500/30 hover:shadow-blue-500/40'
                }`}
              >
                <PlayIcon className="h-4 w-4 mr-2" />
                Start Scraping
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Card */}
      {stats && (
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Statistics</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Total Followers</div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{stats.total.toLocaleString()}</div>
            </div>
            <div>
              <div className="text-sm text-gray-600 dark:text-gray-400">With Avatar</div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{stats.withAvatar.toLocaleString()}</div>
            </div>
            <div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Verified</div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{stats.verified.toLocaleString()}</div>
            </div>
            <div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Can DM</div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{stats.canDm.toLocaleString()}</div>
            </div>
          </div>
        </div>
      )}

      {/* Sessions with Tabs */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {activeTab === 'sessions'
              ? 'Scraping Sessions'
              : activeTab === 'datasets'
                ? 'Scraped Data Sets'
                : 'Scraped usernames'}
          </h2>
          {/* Main Tabs (Sessions vs Data Sets) */}
          <div className="flex items-center space-x-2">
            <div className="flex items-center space-x-1 border border-gray-200 dark:border-gray-700 rounded-lg p-1">
              <button
                onClick={() => setActiveTab('sessions')}
                className={`px-3 py-1.5 text-sm font-medium rounded transition-colors ${
                  activeTab === 'sessions'
                    ? 'bg-blue-500 text-white'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                Sessions
              </button>
              <button
                onClick={() => setActiveTab('datasets')}
                className={`px-3 py-1.5 text-sm font-medium rounded transition-colors flex items-center ${
                  activeTab === 'datasets'
                    ? 'bg-blue-500 text-white'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                <CircleStackIcon className="h-4 w-4 mr-1" />
                Data Sets
              </button>
              <button
                onClick={() => setActiveTab('followers')}
                className={`px-3 py-1.5 text-sm font-medium rounded transition-colors flex items-center ${
                  activeTab === 'followers'
                    ? 'bg-blue-500 text-white'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                <UserGroupIcon className="h-4 w-4 mr-1" />
                Usernames
              </button>
            </div>
            {/* Filter Tabs (only show when Sessions tab is active) */}
            {activeTab === 'sessions' && (
              <div className="flex items-center space-x-2 border-b border-gray-200 dark:border-gray-700 ml-4">
                <button
                  onClick={() => setSessionFilter('all')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    sessionFilter === 'all'
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  All
                </button>
                <button
                  onClick={() => setSessionFilter('active')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    sessionFilter === 'active'
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  Active
                </button>
                <button
                  onClick={() => setSessionFilter('completed')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    sessionFilter === 'completed'
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  Completed
                </button>
                <button
                  onClick={() => setSessionFilter('stopped')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    sessionFilter === 'stopped'
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  Stopped
                </button>
              </div>
            )}
          </div>
        </div>
        
        {/* Phase 5: live pipeline telemetry (queues, CycleTLS pool, funnel, throughput). */}
        {activeTab === 'sessions' && (
          <div className="mb-4">
            <ScraperPipelinePanel
              health={pipelineHealth}
              history={pipelineHistory}
              replicaClaims={replicaClaims}
            />
          </div>
        )}

        {/* Show Sessions or Data Sets based on active tab */}
        {activeTab === 'sessions' ? (
          /* Filter sessions based on selected tab */
          (() => {
            let filteredSessions = allSessions;
            if (sessionFilter === 'active') {
              filteredSessions = allSessions.filter(s => s.status === 'running' || s.status === 'paused');
            } else if (sessionFilter !== 'all') {
              filteredSessions = allSessions.filter(s => s.status === sessionFilter);
            }
            
            return filteredSessions.length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400">No {sessionFilter === 'all' ? '' : sessionFilter} scraping sessions</p>
            ) : (
              <div className="space-y-4">
                {filteredSessions.map((session) => {
                return (
              <div
                key={session._id}
                className="rounded-3xl p-5 border backdrop-blur-xl shadow-sm bg-white/70 dark:bg-slate-900/50 border-slate-200/70 dark:border-slate-700/70"
              >
                 <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                   <div className="min-w-0">
                     <div className="flex items-center flex-wrap gap-2">
                       <h3 className="text-base font-semibold text-slate-900 dark:text-white truncate">
                         {session.name || session.tag}
                       </h3>
                       <SessionStatusChip status={session.status} />
                       {session.accountsInCooldown > 0 && (
                         <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-orange-100/80 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300 backdrop-blur-sm">
                           {session.accountsInCooldown} cool-down
                         </span>
                       )}
                       {session.sourcing?.enabled && (
                         <span
                           className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium backdrop-blur-sm ${
                             session.sourceLoopRunning || session.sourcing?.status === 'running'
                               ? 'bg-violet-100/80 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300'
                               : 'bg-slate-100/80 text-slate-600 dark:bg-slate-700/70 dark:text-slate-300'
                           }`}
                         >
                           <SparklesIcon className="h-3 w-3" />
                           Source{' '}
                           {session.sourcing?.status === 'running' || session.sourceLoopRunning
                             ? 'active'
                             : session.sourcing?.status || 'idle'}
                           {session.sourcing?.totalAdded > 0 && ` · +${session.sourcing.totalAdded}`}
                         </span>
                       )}
                     </div>
                     <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                       <span className="font-medium">Tag:</span> {session.tag} · {session.usernames.length} username(s) · {session.accountIds.length} account(s)
                    </p>
                    {(session.status === 'running' || session.status === 'paused') && (
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <span className="text-xs text-slate-500 dark:text-slate-400">Followers per page (live)</span>
                        <input
                          type="number"
                          min={1}
                          max={200}
                          className="w-[4.5rem] rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1 text-xs text-slate-900 dark:text-white tabular-nums"
                          value={
                            followerPageCountDraft[session.sessionId] !== undefined
                              ? followerPageCountDraft[session.sessionId]
                              : String(session.count ?? 100)
                          }
                          onChange={(e) =>
                            setFollowerPageCountDraft((prev) => ({
                              ...prev,
                              [session.sessionId]: e.target.value
                            }))
                          }
                        />
                        <ScraperActionButton
                          type="button"
                          onClick={() => handleApplyFollowerPageCount(session)}
                          disabled={!!followerPageCountSaving[session.sessionId]}
                          tone="blue"
                        >
                          {followerPageCountSaving[session.sessionId] ? 'Saving…' : 'Apply'}
                        </ScraperActionButton>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {session.status === 'running' && (
                      <>
                        <ScraperActionButton onClick={() => handlePauseSession(session.sessionId)} tone="yellow" icon={PauseIcon}>
                          Pause
                        </ScraperActionButton>
                        <ScraperActionButton onClick={() => handleStopSession(session.sessionId)} tone="red" icon={StopIcon}>
                          Stop
                        </ScraperActionButton>
                      </>
                    )}
                    {session.status === 'paused' && (
                      <>
                        <ScraperActionButton onClick={() => handleResumeSession(session.sessionId)} tone="emerald" icon={PlayIcon}>
                          Resume
                        </ScraperActionButton>
                        <ScraperActionButton onClick={() => handleStopSession(session.sessionId)} tone="red" icon={StopIcon}>
                          Stop
                        </ScraperActionButton>
                      </>
                    )}
                    {(session.status === 'stopped' || session.status === 'completed') && (
                      <ScraperActionButton onClick={() => handleRestartSession(session.sessionId)} tone="blue" icon={ArrowPathIcon}>
                        Restart
                      </ScraperActionButton>
                    )}
                    {['stopped', 'completed', 'failed'].includes(session.status) && (
                      <ScraperActionButton
                        onClick={() =>
                          handleDeleteSession(
                            session.sessionId,
                            session.name || session.tag || session.sessionId
                          )
                        }
                        disabled={deletingSessionId === session.sessionId}
                        tone="red"
                        icon={TrashIcon}
                        title="Remove this session from your list"
                      >
                        {deletingSessionId === session.sessionId ? '…' : 'Delete'}
                      </ScraperActionButton>
                    )}
                    <ScraperActionButton
                      onClick={() => handleFixUsernames(session.sessionId)}
                      disabled={fixingUsernames[session.sessionId]}
                      tone="violet"
                      title="Fix corrupted usernames (e.g., @https://x.com/user → user)"
                    >
                      {fixingUsernames[session.sessionId] ? 'Fixing…' : 'Fix'}
                    </ScraperActionButton>
                    <ScraperActionButton
                      onClick={() => handleRecipientsExport(session.sessionId, 'all')}
                      disabled={recipientExportKey === `${session.sessionId}|all` || recipientExportKey === `${session.sessionId}|new`}
                      tone="slate"
                      icon={ArrowDownTrayIcon}
                      title="Download all stored recipients for this session's tag"
                    >
                      {recipientExportKey === `${session.sessionId}|all` ? '…' : 'Extract all'}
                    </ScraperActionButton>
                    <ScraperActionButton
                      onClick={() => openExtractNewModal(session)}
                      disabled={recipientExportKey === `${session.sessionId}|all` || recipientExportKey === `${session.sessionId}|new`}
                      tone="emerald"
                      icon={ArrowDownTrayIcon}
                      title="Download a capped batch of recipients not yet exported (marked as exported)"
                    >
                      {recipientExportKey === `${session.sessionId}|new` ? '…' : 'Extract new'}
                    </ScraperActionButton>
                  </div>
                </div>

                {session.sourcing?.enabled && (
                  <div className="mt-3">
                    <button
                      type="button"
                      onClick={() => handleToggleSourcingLog(session.sessionId)}
                      className="w-full flex items-center justify-between p-3 bg-violet-50 dark:bg-violet-950/30 hover:bg-violet-100/80 dark:hover:bg-violet-900/25 rounded-2xl border border-violet-200 dark:border-violet-800 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <ChevronDownIcon
                          className={`h-5 w-5 text-violet-600 dark:text-violet-400 transition-transform ${
                            expandedSourcingLog.has(session.sessionId) ? 'rotate-0' : '-rotate-90'
                          }`}
                        />
                        <span className="text-sm font-medium text-violet-900 dark:text-violet-100">
                          Sourcing decisions
                        </span>
                        <span className="text-xs text-violet-600 dark:text-violet-400">
                          (skipped vs added to queue — with reason)
                        </span>
                      </div>
                      {sourcingLogLoading[session.sessionId] && (
                        <ArrowPathIcon className="h-4 w-4 animate-spin text-violet-600" />
                      )}
                    </button>
                    {expandedSourcingLog.has(session.sessionId) && (
                      <div className="mt-2 rounded-2xl border border-violet-200 dark:border-violet-800 overflow-hidden">
                        <div className="flex justify-end p-2 bg-violet-50/80 dark:bg-violet-950/40 border-b border-violet-200 dark:border-violet-800">
                          <button
                            type="button"
                            onClick={() => fetchSourcingDecisions(session.sessionId)}
                            disabled={sourcingLogLoading[session.sessionId]}
                            className="text-xs px-2 py-1 rounded-lg text-violet-700 dark:text-violet-300 hover:bg-violet-200/60 dark:hover:bg-violet-900/50 disabled:opacity-50"
                          >
                            Refresh
                          </button>
                        </div>
                        <div className="max-h-80 overflow-auto">
                          {(sourcingDecisionsBySession[session.sessionId] || []).length === 0 &&
                          !sourcingLogLoading[session.sessionId] ? (
                            <p className="p-4 text-sm text-gray-500 dark:text-gray-400">
                              No decisions recorded yet. They appear as the source loop evaluates timeline users
                              (newest first).
                            </p>
                          ) : (
                            <table className="min-w-full text-xs">
                              <thead className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                                <tr>
                                  <th className="text-left p-2 font-semibold text-gray-700 dark:text-gray-300">Time</th>
                                  <th className="text-left p-2 font-semibold text-gray-700 dark:text-gray-300">Source</th>
                                  <th className="text-left p-2 font-semibold text-gray-700 dark:text-gray-300">Target</th>
                                  <th className="text-left p-2 font-semibold text-gray-700 dark:text-gray-300">Outcome</th>
                                  <th className="text-left p-2 font-semibold text-gray-700 dark:text-gray-300">Reason</th>
                                  <th className="text-left p-2 font-semibold text-gray-700 dark:text-gray-300 w-[1%] whitespace-nowrap">
                                    Queue
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {(sourcingDecisionsBySession[session.sessionId] || []).map((row, idx) => {
                                  const t = row.at ? new Date(row.at) : null;
                                  const timeStr = t && !Number.isNaN(t.getTime()) ? t.toLocaleString() : '—';
                                  const isAdded = row.outcome === 'added';
                                  const target = row.targetUsername || '';
                                  const canQueue =
                                    (session.status === 'running' || session.status === 'paused') && target;
                                  const scrapeKey = sourcingScrapeKey(session.sessionId, target);
                                  const scrapeBusy = !!sourcingScrapeLoading[scrapeKey];
                                  return (
                                    <tr
                                      key={`${row.targetUsername}-${idx}-${timeStr}`}
                                      className="border-b border-gray-100 dark:border-gray-800"
                                    >
                                      <td className="p-2 text-gray-600 dark:text-gray-400 whitespace-nowrap">{timeStr}</td>
                                      <td className="p-2 text-gray-800 dark:text-gray-200">
                                        {row.sourceUsername ? `@${row.sourceUsername}` : '—'}
                                      </td>
                                      <td className="p-2 font-medium text-gray-900 dark:text-gray-100">
                                        @{row.targetUsername}
                                      </td>
                                      <td className="p-2">
                                        <span
                                          className={`inline-flex px-2 py-0.5 rounded-md font-medium ${
                                            isAdded
                                              ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300'
                                              : 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                                          }`}
                                        >
                                          {isAdded ? 'Added' : 'Skipped'}
                                        </span>
                                      </td>
                                      <td className="p-2 text-gray-700 dark:text-gray-300 break-words max-w-[220px]">
                                        {row.reason || '—'}
                                      </td>
                                      <td className="p-2 align-middle">
                                        {!isAdded && canQueue ? (
                                          <button
                                            type="button"
                                            onClick={() =>
                                              handleScrapeFromSourcingDecision(session.sessionId, target)
                                            }
                                            disabled={scrapeBusy}
                                            className="px-2 py-1 rounded-lg text-xs font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                            title="Add this username to the session scrape queue (same as Add Usernames)"
                                          >
                                            {scrapeBusy ? '…' : 'Scrape'}
                                          </button>
                                        ) : !isAdded && !canQueue ? (
                                          <span
                                            className="text-gray-400 dark:text-gray-500 text-[11px]"
                                            title="Start or resume the session to queue usernames"
                                          >
                                            —
                                          </span>
                                        ) : (
                                          <span
                                            className="text-gray-400 dark:text-gray-500 text-[11px]"
                                            title="Already added by sourcing"
                                          >
                                            —
                                          </span>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                {/* ✅ Add Usernames On-The-Go (visible for running/paused sessions) */}
                {(session.status === 'running' || session.status === 'paused') && (
                  <div className="mt-3 p-3 bg-green-50 dark:bg-green-900/10 rounded-2xl border border-green-200 dark:border-green-800">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs font-semibold text-green-800 dark:text-green-300">
                        + Add Usernames
                      </span>
                      <span className="text-xs text-green-600 dark:text-green-400">
                        (idle accounts will automatically pick up new URLs)
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <textarea
                        value={addUsernamesInput[session.sessionId] || ''}
                        onChange={(e) => setAddUsernamesInput(prev => ({ ...prev, [session.sessionId]: e.target.value }))}
                        placeholder="Enter usernames or URLs (one per line or comma-separated)&#10;e.g. username, @username, https://x.com/username"
                        rows={2}
                        className="flex-1 text-sm px-3 py-2 border border-green-300 dark:border-green-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                      />
                      <button
                        onClick={() => handleAddUsernames(session.sessionId)}
                        disabled={addingUsernames[session.sessionId] || !(addUsernamesInput[session.sessionId] || '').trim()}
                        className="self-end px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed rounded-xl transition-colors"
                      >
                        {addingUsernames[session.sessionId] ? 'Adding...' : 'Add'}
                      </button>
                    </div>
                  </div>
                )}

                {session.progress && (
                  <div>
                    {/* Overall progress — compact glass bar */}
                    <div className="mb-4 rounded-2xl p-3.5 border border-slate-200/60 dark:border-slate-700/60 bg-slate-50/70 dark:bg-slate-800/40 backdrop-blur-sm">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">
                          Overall progress
                        </span>
                        <span className="text-xs font-semibold text-slate-600 dark:text-slate-300 tabular-nums">
                          {session.progress.totalFollowers > 0
                            ? Math.round((session.progress.totalScraped / session.progress.totalFollowers) * 100)
                            : 0}
                          % ·{' '}
                          <span className="text-slate-500 dark:text-slate-400">
                            {session.progress.totalScraped.toLocaleString()} /{' '}
                            {session.progress.totalFollowers > 0
                              ? session.progress.totalFollowers.toLocaleString()
                              : '—'}
                          </span>
                        </span>
                      </div>
                      <div className="h-2 w-full rounded-full bg-white/70 dark:bg-slate-900/50 overflow-hidden">
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 transition-all duration-500"
                          style={{
                            width: `${
                              session.progress.totalFollowers > 0
                                ? Math.min((session.progress.totalScraped / session.progress.totalFollowers) * 100, 100)
                                : 0
                            }%`
                          }}
                        />
                      </div>
                      <div className="mt-2 text-[11px] text-slate-500 dark:text-slate-400">
                        {session.progress.completedUsernames || 0} / {session.progress.totalUsernames || 0} URLs completed
                      </div>
                    </div>

                    {/* Pipeline stage cards (Fetch → Country? → Activity? → Stored) */}
                    <ScraperPipelineStages session={session} />
                    
                    
                    {/* Username Details Dropdown - Shows all usernames being scraped */}
                    <div className="mb-4">
                      <button
                        onClick={() => handleToggleUsernameDetails(session.sessionId)}
                        className="w-full flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-2xl transition-colors border border-gray-200 dark:border-gray-600"
                      >
                        <div className="flex items-center space-x-2">
                          <ChevronDownIcon 
                            className={`h-5 w-5 text-gray-500 dark:text-gray-400 transition-transform ${
                              expandedUsernameDetails.has(session.sessionId) ? 'rotate-0' : '-rotate-90'
                            }`}
                          />
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Username Details ({session.usernames?.length || 0} total)
                          </span>
                        </div>
                      </button>
                      
                      {expandedUsernameDetails.has(session.sessionId) && (
                        <div className="mt-2 p-4 bg-gray-50 dark:bg-gray-700/30 rounded-2xl border border-gray-200 dark:border-gray-600">
                          {/* List all usernames with their status */}
                          <div className="space-y-3">
                            {session.usernames?.map((username, idx) => {
                              // ✅ FIX: Use usernameStatus[username].status instead of currentUsername for parallel scraping
                              const usernameResult = session.results?.find(r => r.username === username);
                              // CRITICAL: Always check usernameStatus first - it contains the most up-to-date progress
                              // usernameStatus is updated in real-time during scraping and persists after completion
                              const usernameStatus = session.progress?.usernameStatus?.[username];
                              const isProcessing = usernameStatus?.status === 'processing';
                              
                              // Priority order for followersCount:
                              // 1. usernameStatus.followersCount (real-time updates during scraping)
                              // 2. usernameResult.followersCount (final result after completion)
                              const followersCount = usernameStatus?.followersCount 
                                || usernameResult?.followersCount 
                                || 0;
                              
                              // Priority order for scraped count:
                              // 1. usernameStatus.scraped (real-time updates, persists after completion)
                              // 2. usernameResult.scraped (final result after completion)
                              const scraped = usernameStatus?.scraped !== undefined && usernameStatus.scraped !== null
                                ? usernameStatus.scraped
                                : (usernameResult?.scraped !== undefined && usernameResult.scraped !== null
                                  ? usernameResult.scraped
                                  : 0);
                              
                              // Priority order for stored count:
                              // 1. usernameStatus.stored (real-time updates, persists after completion)
                              // 2. usernameResult.stored (final result after completion)
                              const stored = usernameStatus?.stored !== undefined && usernameStatus.stored !== null
                                ? usernameStatus.stored
                                : (usernameResult?.stored !== undefined && usernameResult.stored !== null ? usernameResult.stored : 0);
                              
                              return (
                                <div key={idx} className="p-3 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                      @{username}
                                    </span>
                                    <span className="text-xs text-gray-500 dark:text-gray-400">
                                      {followersCount > 0 && (
                                        <span className="mr-2">{followersCount.toLocaleString()} followers</span>
                                      )}
                                      {/* Always show stats, even if 0 */}
                                      {scraped.toLocaleString()} scraped, {stored.toLocaleString()} stored
                                    </span>
                                  </div>
                                  
                                  {/* Always show progress bar for all usernames */}
                                  <div className="mb-1">
                                    <div className="flex items-center justify-between mb-1">
                                      <span className="text-xs text-gray-500 dark:text-gray-400">
                                        {scraped.toLocaleString()} / {followersCount > 0 ? followersCount.toLocaleString() : '?'}
                                      </span>
                                      <span className="text-xs font-semibold text-gray-600 dark:text-gray-400">
                                        {followersCount > 0 
                                          ? `${Math.min(Math.round((scraped / followersCount) * 100), 100)}%`
                                          : scraped > 0 
                                            ? 'Calculating...' 
                                            : '0%'
                                        }
                                      </span>
                                    </div>
                                    <div className="w-full rounded-full h-1.5 bg-gray-200 dark:bg-gray-600">
                                      <div
                                        className={`h-1.5 rounded-full transition-all duration-300 ${
                                          scraped >= followersCount && followersCount > 0
                                            ? 'bg-green-600 dark:bg-green-500'
                                            : scraped > 0
                                            ? 'bg-yellow-600 dark:bg-yellow-500'
                                            : 'bg-gray-400 dark:bg-gray-600'
                                        }`}
                                        style={{ 
                                          width: followersCount > 0 
                                            ? `${Math.min((scraped / followersCount) * 100, 100)}%`
                                            : scraped > 0
                                              ? '10%' // Show some progress if we're scraping but don't know total yet
                                              : '0%'
                                        }}
                                      />
                                    </div>
                                  </div>
                                  
                                  {/* Show completed status - only if fully scraped */}
                                  {usernameResult && !isProcessing && scraped >= followersCount && followersCount > 0 && (
                                    <div className="text-xs text-green-600 dark:text-green-400 mt-1">
                                      ✓ Completed
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Blacklisted Usernames Section - Only show if blacklist is enabled and there are blacklisted users */}
                    {session.useBlacklist && session.progress.totalBlacklisted > 0 && (
                      <div className="mb-4">
                        <button
                          onClick={() => {
                            const key = `${session.sessionId}-blacklist`;
                            setExpandedUsernameDetails(prev => {
                              const newSet = new Set(prev);
                              if (newSet.has(key)) {
                                newSet.delete(key);
                              } else {
                                newSet.add(key);
                              }
                              return newSet;
                            });
                          }}
                          className="w-full flex items-center justify-between p-3 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-2xl transition-colors border border-red-200 dark:border-red-800"
                        >
                          <div className="flex items-center space-x-2">
                            <ChevronDownIcon 
                              className={`h-5 w-5 text-red-500 dark:text-red-400 transition-transform ${
                                expandedUsernameDetails.has(`${session.sessionId}-blacklist`) ? 'rotate-0' : '-rotate-90'
                              }`}
                            />
                            <span className="text-sm font-medium text-red-700 dark:text-red-300">
                              Blacklisted Usernames ({session.progress.totalBlacklisted} filtered)
                            </span>
                          </div>
                          <span className="text-xs text-red-600 dark:text-red-400">
                            Click to view
                          </span>
                        </button>
                        
                        {expandedUsernameDetails.has(`${session.sessionId}-blacklist`) && (
                          <div className="mt-2 p-4 bg-red-50 dark:bg-red-900/10 rounded-2xl border border-red-200 dark:border-red-800">
                            <div className="mb-3 text-xs text-red-700 dark:text-red-300">
                              These accounts were skipped because they contain blacklisted names:
                            </div>
                            <div className="space-y-2 max-h-96 overflow-y-auto">
                              {session.progress.blacklistedUsernames?.slice().reverse().map((item, idx) => (
                                <div 
                                  key={idx} 
                                  className="p-2 bg-white dark:bg-gray-800 rounded-lg border border-red-200 dark:border-red-900"
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="flex-1">
                                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                                        @{item.username}
                                      </div>
                                      {item.screenName && item.screenName !== item.username && (
                                        <div className="text-xs text-gray-600 dark:text-gray-400">
                                          {item.screenName}
                                        </div>
                                      )}
                                    </div>
                                    <div className="text-right">
                                      <div className="text-xs font-semibold text-red-600 dark:text-red-400">
                                        Matched: "{item.matchedTerm}"
                                      </div>
                                      <div className="text-xs text-gray-500 dark:text-gray-500">
                                        {new Date(item.timestamp).toLocaleTimeString()}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                            {session.progress.totalBlacklisted > (session.progress.blacklistedUsernames?.length || 0) && (
                              <div className="mt-3 text-xs text-center text-red-600 dark:text-red-400">
                                Showing last {session.progress.blacklistedUsernames?.length || 0} of {session.progress.totalBlacklisted} blacklisted accounts
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Checker-account summary card */}
                    {(session.filters?.filterActiveLastMonth || session.filters?.filterCountryWhitelist) && session.activeCheckerStatus && (
                      <div className="mt-4 rounded-2xl p-3 border border-slate-200/60 dark:border-slate-700/60 bg-slate-50/70 dark:bg-slate-800/40 backdrop-blur-sm">
                        <div className="flex items-center gap-1.5 mb-2 text-xs text-slate-600 dark:text-slate-300">
                          <ClockIcon className="h-3.5 w-3.5" />
                          <span className="font-semibold">Checker accounts (this task)</span>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                          <div className="flex flex-col">
                            <span className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">Total</span>
                            <span className="text-sm font-semibold text-slate-700 dark:text-slate-200 tabular-nums">
                              {session.activeCheckerStatus.total.toLocaleString()}
                            </span>
                          </div>
                          <div className="flex flex-col">
                            <span className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">Available</span>
                            <span className="text-sm font-semibold text-emerald-600 dark:text-emerald-400 tabular-nums">
                              {session.activeCheckerStatus.available.toLocaleString()}
                            </span>
                          </div>
                          <div className="flex flex-col">
                            <span className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">Rate-limited</span>
                            <span className="text-sm font-semibold text-orange-600 dark:text-orange-400 tabular-nums">
                              {session.activeCheckerStatus.rateLimited.toLocaleString()}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
                );
              })}
            </div>
          );
          })()
        ) : activeTab === 'datasets' ? (
          /* Data Sets Tab */
          loadingDataSets && dataSets.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              <span className="ml-3 text-gray-600 dark:text-gray-400">Loading data sets…</span>
            </div>
          ) : dataSets.length === 0 ? (
            <p className="text-gray-500 dark:text-gray-400 py-8 text-center">No scraped data sets found</p>
          ) : (
            <div className="space-y-4">
              {dataSets.map((dataSet, idx) => (
                <div key={idx} className="border border-gray-200 dark:border-gray-700 rounded-2xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900 dark:text-white text-lg flex items-center gap-2">
                        {dataSet.tag || '(no tag)'}
                        {dataSet.statsLoading && (
                          <span className="inline-flex items-center text-xs font-normal text-gray-400">
                            <span className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-500 mr-1" />
                            loading stats…
                          </span>
                        )}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        {dataSet.earliestScrape && dataSet.latestScrape ? (
                          <>
                            Scraped: {new Date(dataSet.earliestScrape).toLocaleDateString()} – {new Date(dataSet.latestScrape).toLocaleDateString()}
                          </>
                        ) : dataSet.latestScrape ? (
                          <>Last scraped: {new Date(dataSet.latestScrape).toLocaleDateString()}</>
                        ) : dataSet.sessions?.[0]?.createdAt ? (
                          <>Session: {new Date(dataSet.sessions[0].createdAt).toLocaleDateString()}</>
                        ) : (
                          'No date information'
                        )}
                      </p>
                      {dataSet.filterSummary?.labels?.length > 0 && (
                        <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-1">
                          Filters: {dataSet.filterSummary.labels.join(' · ')}
                        </p>
                      )}
                      {dataSet.statsPartial && (
                        <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                          Stats still loading — counts may be incomplete for this tag.
                        </p>
                      )}
                      {(dataSet.sourceUsernameCount > 0 || dataSet.sessionProgress?.totalStored > 0) && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {dataSet.sourceUsernameCount > 0 && (
                            <span>{dataSet.sourceUsernameCount.toLocaleString()} source account(s)</span>
                          )}
                          {dataSet.sourceUsernameCount > 0 && dataSet.sessionProgress?.totalStored > 0 && ' · '}
                          {dataSet.sessionProgress?.totalStored > 0 && (
                            <span>Last session stored {Number(dataSet.sessionProgress.totalStored).toLocaleString()}</span>
                          )}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => handleDeleteDataSet(dataSet.tag)}
                      className="ml-4 px-3 py-2 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors flex items-center"
                      title={`Delete tag "${dataSet.tag}" (followers + scrape sessions)`}
                    >
                      <TrashIcon className="h-4 w-4 mr-1" />
                      Delete
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mt-4">
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                      <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Total fetched</div>
                      <div className="text-lg font-semibold text-gray-900 dark:text-white">
                        {dataSet.statsLoading ? (
                          <span className="text-gray-400 animate-pulse">…</span>
                        ) : (
                          (dataSet.totalCount ?? 0).toLocaleString()
                        )}
                      </div>
                    </div>
                    <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-lg p-3">
                      <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Stored (passed)</div>
                      <div className="text-lg font-semibold text-indigo-600 dark:text-indigo-400">
                        {dataSet.statsLoading ? (
                          <span className="text-gray-400 animate-pulse">…</span>
                        ) : (
                          (dataSet.storedCount ?? 0).toLocaleString()
                        )}
                      </div>
                    </div>
                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
                      <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Can DM</div>
                      <div className="text-lg font-semibold text-blue-600 dark:text-blue-400">
                        {dataSet.statsLoading ? (
                          <span className="text-gray-400 animate-pulse">…</span>
                        ) : (
                          (dataSet.canDmCount ?? 0).toLocaleString()
                        )}
                      </div>
                    </div>
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-3">
                      <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Verified</div>
                      <div className="text-lg font-semibold text-yellow-600 dark:text-yellow-400">
                        {dataSet.statsLoading ? (
                          <span className="text-gray-400 animate-pulse">…</span>
                        ) : (
                          (dataSet.verifiedCount ?? 0).toLocaleString()
                        )}
                      </div>
                    </div>
                    <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3">
                      <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">With avatar</div>
                      <div className="text-lg font-semibold text-green-600 dark:text-green-400">
                        {dataSet.statsLoading ? (
                          <span className="text-gray-400 animate-pulse">…</span>
                        ) : (
                          (dataSet.withAvatarCount ?? 0).toLocaleString()
                        )}
                      </div>
                    </div>
                    <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-3">
                      <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Active / stored</div>
                      <div className="text-lg font-semibold text-purple-600 dark:text-purple-400">
                        {dataSet.statsLoading ? (
                          <span className="text-gray-400 animate-pulse">…</span>
                        ) : (
                          (dataSet.activeCount ?? 0).toLocaleString()
                        )}
                      </div>
                    </div>
                  </div>

                  {!dataSet.statsPartial && (dataSet.pendingCount > 0 || dataSet.rejectedCount > 0) && (
                    <div className="flex flex-wrap gap-3 mt-3 text-xs text-gray-500 dark:text-gray-400">
                      {dataSet.pendingCount > 0 && (
                        <span>Pending checks: <strong className="text-gray-700 dark:text-gray-300">{dataSet.pendingCount.toLocaleString()}</strong></span>
                      )}
                      {dataSet.rejectedCount > 0 && (
                        <span>Rejected / inactive: <strong className="text-gray-700 dark:text-gray-300">{dataSet.rejectedCount.toLocaleString()}</strong></span>
                      )}
                    </div>
                  )}
                  
                  {dataSet.sessions && dataSet.sessions.length > 0 && (
                    <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
                      <div className="text-xs text-gray-600 dark:text-gray-400 mb-2 font-semibold">
                        Associated Sessions ({dataSet.sessions.length})
                      </div>
                      <div className="space-y-2">
                        {dataSet.sessions.slice(0, 3).map((session) => (
                          <div key={session._id} className="text-sm text-gray-600 dark:text-gray-400">
                            • {session.name || session.sessionId} ({session.status})
                            {session.startedAt && (
                              <span className="ml-2 text-xs">
                                - {new Date(session.startedAt).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        ))}
                        {dataSet.sessions.length > 3 && (
                          <div className="text-xs text-gray-500 dark:text-gray-500">
                            + {dataSet.sessions.length - 3} more session(s)
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
                    <button
                      type="button"
                      onClick={() => toggleDataSetSourcesExpanded(dataSet.tag)}
                      className="flex items-center w-full text-left text-sm font-semibold text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400"
                    >
                      <ChevronDownIcon
                        className={`h-4 w-4 mr-2 shrink-0 transition-transform ${
                          expandedDataSetTags.has(dataSet.tag) ? 'rotate-180' : ''
                        }`}
                      />
                      Scraped source usernames
                      <span className="ml-2 font-normal text-gray-500 dark:text-gray-400">
                        (accounts you scraped followers from)
                      </span>
                    </button>

                    {expandedDataSetTags.has(dataSet.tag) && (
                      <div className="mt-3 space-y-3">
                        <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                          <input
                            type="text"
                            placeholder="Filter by source username prefix…"
                            value={sourceSearchDraftByTag[dataSet.tag] ?? ''}
                            onChange={(e) =>
                              setSourceSearchDraftByTag((prev) => ({
                                ...prev,
                                [dataSet.tag]: e.target.value
                              }))
                            }
                            className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-white"
                          />
                          <button
                            type="button"
                            onClick={() =>
                              loadSourceUsernamesForTag(dataSet.tag, {
                                page: 1,
                                search: sourceSearchDraftByTag[dataSet.tag] ?? ''
                              })
                            }
                            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg"
                          >
                            Search
                          </button>
                        </div>

                        {sourceUsernamesByTag[dataSet.tag]?.error && (
                          <p className="text-sm text-red-600 dark:text-red-400">
                            {sourceUsernamesByTag[dataSet.tag].error}
                          </p>
                        )}

                        {sourceUsernamesByTag[dataSet.tag]?.loading ? (
                          <div className="flex items-center py-6 text-gray-500 dark:text-gray-400 text-sm">
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500 mr-2" />
                            Loading source usernames…
                          </div>
                        ) : (
                          <>
                            {(sourceUsernamesByTag[dataSet.tag]?.items || []).length === 0 ? (
                              <p className="text-sm text-gray-500 dark:text-gray-400 py-2">
                                No source usernames match this filter.
                              </p>
                            ) : (
                              <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-600">
                                <table className="min-w-full text-sm">
                                  <thead className="bg-gray-50 dark:bg-gray-700/80">
                                    <tr>
                                      <th className="text-left px-3 py-2 font-medium text-gray-700 dark:text-gray-200">
                                        Source username
                                      </th>
                                      <th className="text-right px-3 py-2 font-medium text-gray-700 dark:text-gray-200">
                                        Followers stored
                                      </th>
                                      <th className="text-right px-3 py-2 font-medium text-gray-700 dark:text-gray-200 w-28">
                                        Action
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                                    {(sourceUsernamesByTag[dataSet.tag]?.items || []).map((row, ri) => (
                                      <tr
                                        key={`${row.sourceUsername ?? '__none__'}-${ri}`}
                                        className="bg-white dark:bg-gray-800/50"
                                      >
                                        <td className="px-3 py-2 text-gray-900 dark:text-gray-100 font-mono">
                                          {row.sourceUsername ? `@${row.sourceUsername}` : (
                                            <span className="text-gray-500 italic">Unknown source</span>
                                          )}
                                        </td>
                                        <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">
                                          {Number(row.followerCount).toLocaleString()}
                                        </td>
                                        <td className="px-3 py-2 text-right">
                                          <button
                                            type="button"
                                            onClick={() =>
                                              handleDeleteSourceUsernameRow(
                                                dataSet.tag,
                                                row.sourceUsername,
                                                row.followerCount
                                              )
                                            }
                                            className="inline-flex items-center px-2 py-1 text-xs font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                                          >
                                            <TrashIcon className="h-3.5 w-3.5 mr-1" />
                                            Delete
                                          </button>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}

                            {sourceUsernamesByTag[dataSet.tag] &&
                              sourceUsernamesByTag[dataSet.tag].total > 0 && (
                                <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-gray-600 dark:text-gray-400">
                                  <span>
                                    Page {sourceUsernamesByTag[dataSet.tag].page} of{' '}
                                    {Math.max(
                                      1,
                                      Math.ceil(
                                        sourceUsernamesByTag[dataSet.tag].total /
                                          sourceUsernamesByTag[dataSet.tag].limit
                                      )
                                    )}{' '}
                                    · {sourceUsernamesByTag[dataSet.tag].total.toLocaleString()} source(s)
                                  </span>
                                  <div className="flex gap-2">
                                    <button
                                      type="button"
                                      disabled={
                                        !sourceUsernamesByTag[dataSet.tag] ||
                                        sourceUsernamesByTag[dataSet.tag].page <= 1
                                      }
                                      onClick={() =>
                                        loadSourceUsernamesForTag(dataSet.tag, {
                                          page: (sourceUsernamesByTag[dataSet.tag].page || 1) - 1,
                                          search: sourceUsernamesByTag[dataSet.tag].searchApplied ?? ''
                                        })
                                      }
                                      className="px-2 py-1 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-40"
                                    >
                                      Previous
                                    </button>
                                    <button
                                      type="button"
                                      disabled={
                                        !sourceUsernamesByTag[dataSet.tag] ||
                                        sourceUsernamesByTag[dataSet.tag].page * sourceUsernamesByTag[dataSet.tag].limit >=
                                          sourceUsernamesByTag[dataSet.tag].total
                                      }
                                      onClick={() =>
                                        loadSourceUsernamesForTag(dataSet.tag, {
                                          page: (sourceUsernamesByTag[dataSet.tag].page || 1) + 1,
                                          search: sourceUsernamesByTag[dataSet.tag].searchApplied ?? ''
                                        })
                                      }
                                      className="px-2 py-1 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-40"
                                    >
                                      Next
                                    </button>
                                  </div>
                                </div>
                              )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )
        ) : (
          /* Usernames tab — ScrapedSourceUsername targets + follower counts (see GET /scraper/scrape-sources) */
          <div className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Target handles you started or that <span className="font-medium">sourcing</span> added (same list used to
              avoid duplicate scrapes). <span className="font-medium">Follower rows</span> counts stored data; new
              targets may show 0 until scraping finishes. With a tag filter, only handles that already have follower
              data in that tag are listed. Removing a row deletes stored followers for that handle (and tag if set). Use{' '}
              <span className="font-medium">Data Sets</span> to delete an entire tag at once.
            </p>
            <div className="flex flex-wrap items-center gap-3 justify-between">
              <label className="text-sm text-gray-700 dark:text-gray-300 flex items-center gap-2">
                <span>Filter by tag</span>
                <select
                  value={scrapeSourcesTagFilter}
                  onChange={(e) => {
                    setScrapeSourcesTagFilter(e.target.value);
                    setSelectedScrapeSourceKeys([]);
                    setScrapeSourcesPage(1);
                  }}
                  className="text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-2 py-1.5 min-w-[10rem]"
                >
                  <option value="">All tags</option>
                  {(stats?.tags || []).map((t) => (
                    <option key={t || '__empty__'} value={t || ''}>
                      {t || '(no tag)'}
                    </option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                onClick={handleSelectAllScrapeSourcesMatchingFilter}
                disabled={
                  scrapeSourcesSelectAllMatchingLoading ||
                  bulkDeletingScrapeSources ||
                  (scrapeSourcesPagination.total ?? 0) <= 0
                }
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-950/40 hover:bg-blue-100 dark:hover:bg-blue-900/50 disabled:opacity-50 rounded-xl transition-colors border border-blue-200 dark:border-blue-800"
              >
                {scrapeSourcesSelectAllMatchingLoading
                  ? 'Loading…'
                  : `Select all matching (${(scrapeSourcesPagination.total ?? 0).toLocaleString()})`}
              </button>
              {selectedScrapeSourceKeys.length > 0 && (
                <button
                  type="button"
                  onClick={handleBulkDeleteScrapeSources}
                  disabled={bulkDeletingScrapeSources}
                  className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 rounded-xl transition-colors"
                >
                  <TrashIcon className="h-4 w-4 mr-2" />
                  {bulkDeletingScrapeSources
                    ? 'Deleting…'
                    : `Delete selected (${selectedScrapeSourceKeys.length.toLocaleString()})`}
                </button>
              )}
            </div>
            {scrapeSourcesLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
                <span className="ml-3 text-gray-600 dark:text-gray-400">Loading scrape sources…</span>
              </div>
            ) : scrapeSourcesList.length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400 py-8 text-center">
                No targets in history for this filter (try &quot;All tags&quot;, or nothing has been queued yet)
              </p>
            ) : (
              <>
                <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
                      <tr>
                        <th className="w-10 p-3 align-middle">
                          <input
                            ref={scrapeSourcesSelectAllRef}
                            type="checkbox"
                            checked={allScrapeSourcesPageSelected}
                            onChange={toggleSelectAllScrapeSourcesOnPage}
                            disabled={bulkDeletingScrapeSources || scrapeSourcesList.length === 0}
                            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800"
                            title="Select or clear all targets on this page only (use “Select all matching” for every row in the filter)"
                            aria-label="Select all scrape targets on this page only"
                          />
                        </th>
                        <th className="text-left p-3 font-semibold text-gray-700 dark:text-gray-300">Source</th>
                        <th className="text-right p-3 font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap">
                          Follower rows
                        </th>
                        <th className="text-left p-3 font-semibold text-gray-700 dark:text-gray-300">Tags</th>
                        <th className="text-left p-3 font-semibold text-gray-700 dark:text-gray-300">Last scraped</th>
                        <th className="text-right p-3 font-semibold text-gray-700 dark:text-gray-300 w-[1%]"> </th>
                      </tr>
                    </thead>
                    <tbody>
                      {scrapeSourcesList.map((row) => {
                        const rowId = scrapeSourceRowKey(row);
                        const busy = deletingScrapeSourceId === rowId;
                        const rowSelected = selectedScrapeSourceKeys.includes(rowId);
                        const tags = row.tags || [];
                        const tagsDisp =
                          tags.length === 0
                            ? '—'
                            : tags.length <= 2
                              ? tags
                                  .map((t) => (t == null || String(t).trim() === '' ? '(no tag)' : t))
                                  .join(', ')
                              : `${tags
                                  .slice(0, 2)
                                  .map((t) => (t == null || String(t).trim() === '' ? '(no tag)' : t))
                                  .join(', ')} +${tags.length - 2}`;
                        return (
                          <tr
                            key={rowId}
                            className={`border-b border-gray-100 dark:border-gray-800 last:border-0 ${
                              rowSelected ? 'bg-blue-50/60 dark:bg-blue-950/25' : ''
                            }`}
                          >
                            <td className="p-3 align-middle">
                              <input
                                type="checkbox"
                                checked={rowSelected}
                                onChange={() => toggleScrapeSourceKeySelected(rowId)}
                                disabled={bulkDeletingScrapeSources}
                                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800"
                                aria-label={`Select @${row.sourceUsername}`}
                              />
                            </td>
                            <td className="p-3 font-medium text-gray-900 dark:text-white">@{row.sourceUsername}</td>
                            <td className="p-3 text-right text-gray-700 dark:text-gray-300 tabular-nums">
                              {(row.followerCount ?? 0).toLocaleString()}
                            </td>
                            <td className="p-3 text-gray-600 dark:text-gray-400 max-w-[14rem] truncate" title={tagsDisp}>
                              {tagsDisp}
                            </td>
                            <td className="p-3 text-gray-600 dark:text-gray-400 whitespace-nowrap">
                              {row.latestAt ? new Date(row.latestAt).toLocaleString() : '—'}
                            </td>
                            <td className="p-3 text-right">
                              <button
                                type="button"
                                onClick={() => handleDeleteScrapeSourceRow(row)}
                                disabled={busy || bulkDeletingScrapeSources}
                                className="inline-flex items-center px-2 py-1 text-xs font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg disabled:opacity-50"
                              >
                                <TrashIcon className="h-3.5 w-3.5 mr-1" />
                                {busy ? '…' : 'Remove'}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {scrapeSourcesPagination.pages > 1 && (
                  <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
                    <span>
                      Page {scrapeSourcesPage} of {scrapeSourcesPagination.pages} (
                      {scrapeSourcesPagination.total?.toLocaleString?.() ?? scrapeSourcesPagination.total} total)
                    </span>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        disabled={scrapeSourcesPage <= 1}
                        onClick={() => setScrapeSourcesPage((p) => Math.max(1, p - 1))}
                        className="px-3 py-1 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-40"
                      >
                        Previous
                      </button>
                      <button
                        type="button"
                        disabled={scrapeSourcesPage >= scrapeSourcesPagination.pages}
                        onClick={() => setScrapeSourcesPage((p) => p + 1)}
                        className="px-3 py-1 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-40"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Extract New Modal — capped export with (accounts × follows) sizing. */}
      {extractNewModal && (() => {
        const s = extractNewModal;
        const stats = extractNewStats[s.sessionId];
        const accountsNum = Math.max(0, parseInt(s.accounts, 10) || 0);
        const followsNum = Math.max(0, parseInt(s.follows, 10) || 0);
        const requested = accountsNum * followsNum;
        const available = stats?.available ?? null;
        const effective = available != null ? Math.min(requested, available) : requested;
        const inFlight = recipientExportKey === `${s.sessionId}|new`;
        const canSubmit = !inFlight && accountsNum > 0 && followsNum > 0;
        return (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div
                className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity"
                onClick={() => !inFlight && setExtractNewModal(null)}
              />
              <div
                className={[
                  'relative inline-block align-bottom text-left transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full',
                  'rounded-3xl shadow-2xl border backdrop-blur-xl',
                  'bg-white/80 dark:bg-slate-900/70 border-slate-200/70 dark:border-slate-700/70',
                ].join(' ')}
              >
                <div className="px-5 pt-5 pb-5 sm:p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <ArrowDownTrayIcon className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                        <h3 className="text-base font-semibold text-slate-900 dark:text-white">
                          Extract new
                        </h3>
                      </div>
                      <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                        Session: <span className="font-medium text-slate-700 dark:text-slate-300">{s.name}</span>
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => !inFlight && setExtractNewModal(null)}
                      disabled={inFlight}
                      className="h-7 w-7 inline-flex items-center justify-center rounded-full text-slate-500 hover:bg-slate-200/60 dark:text-slate-400 dark:hover:bg-slate-700/60 disabled:opacity-40"
                      aria-label="Close"
                    >
                      ×
                    </button>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                        Amount of accounts
                      </label>
                      <input
                        type="number"
                        min="1"
                        step="1"
                        value={s.accounts}
                        onChange={(e) =>
                          setExtractNewModal(prev => (prev ? { ...prev, accounts: e.target.value } : prev))
                        }
                        className="w-full h-10 px-3 rounded-xl border text-sm tabular-nums bg-white/70 dark:bg-slate-800/60 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500/60 focus:border-transparent"
                        placeholder="1000"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                        Follows per account
                      </label>
                      <input
                        type="number"
                        min="1"
                        step="1"
                        value={s.follows}
                        onChange={(e) =>
                          setExtractNewModal(prev => (prev ? { ...prev, follows: e.target.value } : prev))
                        }
                        className="w-full h-10 px-3 rounded-xl border text-sm tabular-nums bg-white/70 dark:bg-slate-800/60 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500/60 focus:border-transparent"
                        placeholder="48"
                      />
                      <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
                        Default: 48.
                      </p>
                    </div>

                    {/* Summary card */}
                    <div className="mt-1 rounded-2xl p-3 border border-slate-200/60 dark:border-slate-700/60 bg-slate-50/70 dark:bg-slate-800/40 backdrop-blur-sm">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="flex flex-col">
                          <span className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
                            Requested
                          </span>
                          <span className="text-sm font-semibold text-slate-800 dark:text-slate-100 tabular-nums">
                            {requested.toLocaleString()}
                          </span>
                        </div>
                        <div className="flex flex-col">
                          <span className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
                            Available
                          </span>
                          <span className="text-sm font-semibold text-emerald-600 dark:text-emerald-400 tabular-nums">
                            {extractNewStatsLoading && !stats
                              ? '…'
                              : available != null
                              ? available.toLocaleString()
                              : '—'}
                          </span>
                        </div>
                        <div className="flex flex-col col-span-2 pt-2 border-t border-slate-200/60 dark:border-slate-700/60">
                          <span className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
                            Will export
                          </span>
                          <span className="text-base font-bold text-emerald-600 dark:text-emerald-400 tabular-nums">
                            {effective.toLocaleString()}
                          </span>
                          {stats && (
                            <span className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                              {stats.exported.toLocaleString()} already exported ·{' '}
                              {stats.total.toLocaleString()} total
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <p className="text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed">
                      Exported rows are marked in the database. The next &quot;Extract new&quot; with the same
                      numbers will continue from the next batch and skip already-exported usernames.
                    </p>
                  </div>

                  <div className="mt-5 flex items-center justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => !inFlight && setExtractNewModal(null)}
                      disabled={inFlight}
                      className="inline-flex items-center gap-1.5 h-8 px-3 rounded-xl text-xs font-medium border backdrop-blur-sm text-slate-700 border-slate-300 hover:bg-slate-100/70 dark:text-slate-200 dark:border-slate-600 dark:hover:bg-slate-700/40 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      disabled={!canSubmit}
                      onClick={async () => {
                        const ok = await handleRecipientsExport(s.sessionId, 'new', {
                          accounts: accountsNum,
                          follows: followsNum,
                        });
                        if (ok) {
                          fetchExtractNewStats(s.sessionId);
                          setExtractNewModal(null);
                        }
                      }}
                      className="inline-flex items-center gap-1.5 h-8 px-3 rounded-xl text-xs font-semibold border backdrop-blur-sm text-white bg-emerald-600 border-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ArrowDownTrayIcon className="h-3.5 w-3.5" />
                      {inFlight
                        ? recipientExportProgress && recipientExportProgress.total > 0
                          ? `${recipientExportProgress.done.toLocaleString()} / ${recipientExportProgress.total.toLocaleString()}`
                          : 'Exporting…'
                        : 'Export'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Start Scraping Modal */}
      {showStartModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowStartModal(false)}></div>
            
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              <div className="px-4 pt-5 pb-4 sm:p-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Start Scraping</h3>
                
                 <div className="space-y-4">
                   {/* Task Name Input */}
                   <div>
                     <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                       Task Name <span className="text-red-500">*</span>
                     </label>
                     <input
                       type="text"
                       value={taskName}
                       onChange={(e) => setTaskName(e.target.value)}
                       placeholder="e.g., Task 1 - 26-11-2025"
                       className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                     />
                     <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                       A descriptive name for this scraping task (will be used for mass DM selection)
                     </p>
                   </div>

                   {/* Usernames Input */}
                   <div>
                     <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                       Usernames (one per line) {!sourcingEnabled && <span className="text-red-500">*</span>}
                     </label>
                    <textarea
                      value={usernames}
                      onChange={(e) => setUsernames(e.target.value)}
                      placeholder="Art_mishi_&#10;https://x.com/username/&#10;x.com/username/"
                      rows={6}
                      className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white ${
                        usernameValidation.errors.length > 0 
                          ? 'border-red-500 dark:border-red-500' 
                          : 'border-gray-300 dark:border-gray-600'
                      }`}
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Enter usernames or URLs (one per line). Supports: username, https://x.com/username/, x.com/username/
                    </p>
                    
                    {/* Validation Messages */}
                    {usernameValidation.checking && (
                      <p className="mt-1 text-xs text-blue-500 dark:text-blue-400">
                        Checking usernames...
                      </p>
                    )}
                    
                    {usernameValidation.errors.length > 0 && (
                      <div className="mt-2 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded">
                        <p className="text-xs font-semibold text-red-700 dark:text-red-400 mb-1">
                          ⚠️ Currently Being Scraped:
                        </p>
                        <ul className="text-xs text-red-600 dark:text-red-300 list-disc list-inside">
                          {usernameValidation.errors.map((error, idx) => (
                            <li key={idx}>{error.username} - {error.message}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {usernameValidation.warnings.length > 0 && (
                      <div className="mt-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded">
                        <p className="text-xs font-semibold text-yellow-700 dark:text-yellow-400 mb-1">
                          ℹ️ Previously Scraped:
                        </p>
                        <ul className="text-xs text-yellow-600 dark:text-yellow-300 list-disc list-inside">
                          {usernameValidation.warnings.map((warning, idx) => (
                            <li key={idx}>{warning.username} - {warning.message}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Tag Input */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Tag <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={tag}
                      onChange={(e) => setTag(e.target.value)}
                      placeholder="Enter a tag for this scraping session"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>

                  {/* Count Input */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Count per Request
                    </label>
                    <input
                      type="number"
                      value={count}
                      onChange={(e) => setCount(e.target.value)}
                      min={1}
                      max={200}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>

                  {/* Account Selection */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Select Accounts <span className="text-red-500">*</span>
                      </label>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {selectedAccountIds.length} of {accounts.length} selected
                      </span>
                    </div>
                    <div className="border border-gray-300 dark:border-gray-600 rounded-md p-3 max-h-96 overflow-y-auto">
                      {accounts.length === 0 ? (
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          No accounts with "Scraper" tag found. Please tag accounts first.
                        </p>
                      ) : (
                        <>
                          <div className="space-y-2">
                            <label className="flex items-center gap-2 cursor-pointer pb-2 border-b border-gray-200 dark:border-gray-700 mb-2">
                              <input
                                type="checkbox"
                                checked={(() => {
                                  const selectableAccounts = accounts.filter(acc => acc.hasValidSession && !acc.inUse);
                                  return selectableAccounts.length > 0 && 
                                    selectableAccounts.every(acc => selectedAccountIds.includes(acc._id));
                                })()}
                                onChange={handleSelectAllAccounts}
                                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                              />
                              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Select All Available
                              </span>
                            </label>
                            
                            {/* Groups Section */}
                            {(() => {
                              const accountGroups = [...new Set(accounts
                                .map(acc => acc.group)
                                .filter(g => g != null && String(g).trim() !== '')
                                .map(g => String(g).trim()))];
                              
                              if (accountGroups.length === 0) return null;
                              
                              return (
                                <div className="space-y-1 mb-3 pb-3 border-b border-gray-200 dark:border-gray-700">
                                  <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                                    Groups
                                  </div>
                                  {accountGroups.sort().map((groupName) => {
                                    const groupAccounts = accounts.filter(acc => {
                                      if (!acc.group) return false;
                                      return String(acc.group).trim() === groupName;
                                    });
                                    const selectableGroupAccounts = groupAccounts.filter(acc => 
                                      acc.hasValidSession === true && acc.inUse !== true
                                    );
                                    const groupAccountIds = selectableGroupAccounts.map(acc => acc._id);
                                    const allGroupSelected = selectableGroupAccounts.length > 0 && 
                                      groupAccountIds.every(id => selectedAccountIds.includes(id));
                                    const isExpanded = expandedGroups.has(groupName);
                                    
                                    return (
                                      <div key={groupName} className="space-y-1">
                                        <div className="flex items-center justify-between">
                                          <div className="flex items-center flex-1 gap-2">
                                            <button
                                              type="button"
                                              onClick={() => handleToggleGroup(groupName)}
                                              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                            >
                                              {isExpanded ? (
                                                <ChevronDownIcon className="h-4 w-4" />
                                              ) : (
                                                <ChevronDownIcon className="h-4 w-4 transform -rotate-90" />
                                              )}
                                            </button>
                                            <input
                                              type="checkbox"
                                              checked={allGroupSelected}
                                              onChange={() => handleSelectGroup(groupName)}
                                              disabled={selectableGroupAccounts.length === 0}
                                              className={`rounded border-gray-300 text-primary-600 focus:ring-primary-500 ${
                                                selectableGroupAccounts.length === 0 ? 'opacity-50 cursor-not-allowed' : ''
                                              }`}
                                            />
                                            <span className={`text-sm font-medium ${
                                              selectableGroupAccounts.length === 0
                                                ? 'text-gray-400 dark:text-gray-500'
                                                : 'text-gray-700 dark:text-gray-300'
                                            }`}>
                                              {groupName} ({selectableGroupAccounts.length}/{groupAccounts.length})
                                            </span>
                                          </div>
                                        </div>
                                        {isExpanded && (
                                          <div className="ml-6 space-y-1 mt-1">
                                            {groupAccounts.map((account) => {
                                              const hasValidSession = account.hasValidSession === true;
                                              const inUse = account.inUse === true;
                                              const isDisabled = !hasValidSession || inUse;
                                              return (
                                                <label key={account._id} className="flex items-center justify-between">
                                                  <div className="flex items-center flex-1">
                                                    <input
                                                      type="checkbox"
                                                      checked={selectedAccountIds.includes(account._id)}
                                                      onChange={() => toggleAccountSelection(account._id)}
                                                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                                      disabled={isDisabled}
                                                    />
                                                    <span className={`ml-2 text-sm ${isDisabled ? 'text-gray-400 dark:text-gray-500' : 'text-gray-700 dark:text-gray-300'}`}>
                                                      {account.displayName || account.username}
                                                    </span>
                                                  </div>
                                                  {!hasValidSession && (
                                                    <span className="ml-2 text-xs text-red-600 dark:text-red-400" title="Run Login Test for this account">
                                                      ⚠️ No Session
                                                    </span>
                                                  )}
                                                  {hasValidSession && inUse && (
                                                    <span className="ml-2 text-xs text-orange-600 dark:text-orange-400" title="Account is currently being used in an active scraping task">
                                                      ⚠️ In Use
                                                    </span>
                                                  )}
                                                  {hasValidSession && !inUse && (
                                                    <span className="ml-2 text-xs text-green-600 dark:text-green-400" title="Account has valid session data">
                                                      ✓ Ready
                                                    </span>
                                                  )}
                                                </label>
                                              );
                                            })}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              );
                            })()}
                            
                            {/* Ungrouped Accounts */}
                            {(() => {
                              const ungroupedAccounts = accounts.filter(acc => !acc.group || acc.group.trim() === '');
                              if (ungroupedAccounts.length === 0) return null;
                              
                              return (
                                <div className="space-y-1">
                                  {(() => {
                                    const accountGroups = [...new Set(accounts
                                      .map(acc => acc.group)
                                      .filter(g => g != null && String(g).trim() !== ''))];
                                    if (accountGroups.length > 0) {
                                      return (
                                        <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                                          Ungrouped
                                        </div>
                                      );
                                    }
                                    return null;
                                  })()}
                                  {ungroupedAccounts.map((account) => {
                                    const hasValidSession = account.hasValidSession === true;
                                    const inUse = account.inUse === true;
                                    const isDisabled = !hasValidSession || inUse;
                                    return (
                                      <label key={account._id} className="flex items-center justify-between">
                                        <div className="flex items-center flex-1">
                                          <input
                                            type="checkbox"
                                            checked={selectedAccountIds.includes(account._id)}
                                            onChange={() => toggleAccountSelection(account._id)}
                                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                            disabled={isDisabled}
                                          />
                                          <span className={`ml-2 text-sm ${isDisabled ? 'text-gray-400 dark:text-gray-500' : 'text-gray-700 dark:text-gray-300'}`}>
                                            {account.displayName || account.username}
                                          </span>
                                        </div>
                                        {!hasValidSession && (
                                          <span className="ml-2 text-xs text-red-600 dark:text-red-400" title="Run Login Test for this account">
                                            ⚠️ No Session
                                          </span>
                                        )}
                                        {hasValidSession && inUse && (
                                          <span className="ml-2 text-xs text-orange-600 dark:text-orange-400" title="Account is currently being used in an active scraping task">
                                            ⚠️ In Use
                                          </span>
                                        )}
                                        {hasValidSession && !inUse && (
                                          <span className="ml-2 text-xs text-green-600 dark:text-green-400" title="Account has valid session data">
                                            ✓ Ready
                                          </span>
                                        )}
                                      </label>
                                    );
                                  })}
                                </div>
                              );
                            })()}
                          </div>
                          {accounts.some(acc => !acc.hasValidSession) && (
                            <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
                              ⚠️ Accounts without valid sessions need a "Login Test" before scraping
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {/* Filters */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                      Filters (only matching followers will be stored)
                    </label>
                    <div className="space-y-3">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={filters.filterAvatar}
                          onChange={(e) => setFilters({ ...filters, filterAvatar: e.target.checked })}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="ml-2 text-sm text-gray-700 dark:text-gray-300 flex items-center">
                          <PhotoIcon className="h-4 w-4 mr-1" />
                          Only store users with profile picture
                        </span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={filters.filterDMs}
                          onChange={(e) => setFilters({ ...filters, filterDMs: e.target.checked })}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="ml-2 text-sm text-gray-700 dark:text-gray-300 flex items-center">
                          <ChatBubbleLeftRightIcon className="h-4 w-4 mr-1" />
                          Only store users who accept DMs
                        </span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={filters.filterVerified}
                          onChange={(e) => setFilters({ ...filters, filterVerified: e.target.checked })}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="ml-2 text-sm text-gray-700 dark:text-gray-300 flex items-center">
                          <ShieldCheckIcon className="h-4 w-4 mr-1" />
                          Only store verified users
                        </span>
                      </label>
                      {(user?.role === 'admin' || user?.features?.canUseActiveCheckers) && (
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={filters.filterActiveLastMonth}
                          onChange={(e) => setFilters({ ...filters, filterActiveLastMonth: e.target.checked })}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="ml-2 text-sm text-gray-700 dark:text-gray-300 flex items-center">
                          <ClockIcon className="h-4 w-4 mr-1" />
                          Only store users active in the last month
                        </span>
                      </label>
                      )}
                      {(user?.role === 'admin' || user?.features?.canUseActiveCheckers) && (
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={filters.filterCountryWhitelist}
                          onChange={(e) => {
                            const on = e.target.checked;
                            setFilters({ ...filters, filterCountryWhitelist: on });
                            if (on && allowedCountriesList.length === 0) {
                              setAllowedCountriesList([...DEFAULT_SCRAPER_COUNTRY_WHITELIST]);
                            }
                          }}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="ml-2 text-sm text-gray-700 dark:text-gray-300 flex items-center">
                          <UserGroupIcon className="h-4 w-4 mr-1" />
                          Only store users whose &quot;Based in&quot; country is in your whitelist (About tab)
                        </span>
                      </label>
                      )}
                      {filters.filterCountryWhitelist && (user?.role === 'admin' || user?.features?.canUseActiveCheckers) && (
                        <div className="pl-6 border-l-2 border-primary-200 dark:border-primary-800 space-y-2">
                          <p className="text-xs text-gray-600 dark:text-gray-400">
                            Country names must match X’s About label for spelling; comparison is case-insensitive (United States, united states, and UNITED STATES are the same). Your active checker accounts run these checks in a background queue.
                          </p>
                          <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto p-2 rounded-md bg-gray-50 dark:bg-gray-800/80 border border-gray-200 dark:border-gray-600">
                            {allowedCountriesList.map((c) => (
                              <span
                                key={c}
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-primary-100 dark:bg-primary-900/40 text-primary-800 dark:text-primary-200"
                              >
                                {c}
                                <button
                                  type="button"
                                  onClick={() => setAllowedCountriesList(allowedCountriesList.filter((x) => x !== c))}
                                  className="hover:text-red-600 dark:hover:text-red-400 font-semibold"
                                  aria-label={`Remove ${c}`}
                                >
                                  ×
                                </button>
                              </span>
                            ))}
                          </div>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={countryAddInput}
                              onChange={(e) => setCountryAddInput(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  const t = countryAddInput.trim();
                                  if (t && !allowedCountriesList.includes(t)) {
                                    setAllowedCountriesList([...allowedCountriesList, t]);
                                    setCountryAddInput('');
                                  }
                                }
                              }}
                              placeholder="Add country (spelling as on X; any case)"
                              className="flex-1 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1.5 text-sm"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                const t = countryAddInput.trim();
                                if (t && !allowedCountriesList.includes(t)) {
                                  setAllowedCountriesList([...allowedCountriesList, t]);
                                  setCountryAddInput('');
                                }
                              }}
                              className="px-3 py-1.5 text-sm rounded-md bg-primary-600 text-white hover:bg-primary-700"
                            >
                              Add
                            </button>
                            <button
                              type="button"
                              onClick={() => setAllowedCountriesList([...DEFAULT_SCRAPER_COUNTRY_WHITELIST])}
                              className="px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300"
                            >
                              Reset default list
                            </button>
                          </div>
                        </div>
                      )}
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={useBlacklist}
                          onChange={(e) => setUseBlacklist(e.target.checked)}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="ml-2 text-sm text-gray-700 dark:text-gray-300 flex items-center">
                          <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                          </svg>
                          Use name blacklist (skip accounts with blacklisted names)
                        </span>
                      </label>
                    </div>
                    <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                      Note: Only followers matching the selected filters will be stored in the database.
                      {useBlacklist && " Accounts with blacklisted names in username/screen name will be skipped immediately."}
                    </p>
                    {(filters.filterActiveLastMonth || filters.filterCountryWhitelist) && (user?.role === 'admin' || user?.features?.canUseActiveCheckers) && (
                      <>
                        <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                          ⚠️ Activity and country filters add API calls per follower. Use dedicated active checker accounts (your accounts only) for parallel processing.
                        </p>
                        
                        {/* Active Checker Account Selection */}
                        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
                          <div className="flex items-center justify-between mb-2">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                              Select active checker accounts <span className="text-red-500">*</span>
                            </label>
                            <span className="text-sm text-gray-500 dark:text-gray-400">
                              {selectedActiveCheckerAccountIds.length} of {activeCheckerAccounts.length} selected
                            </span>
                          </div>
                          <div className="border border-gray-300 dark:border-gray-600 rounded-md p-3 max-h-96 overflow-y-auto">
                            {activeCheckerAccounts.length === 0 ? (
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                No accounts with "active_checker" tag found. Please tag accounts first in /dashboard/accounts.
                              </p>
                            ) : (
                              <div className="space-y-2">
                                {/* Select All */}
                                <label className="flex items-center gap-2 cursor-pointer pb-2 border-b border-gray-200 dark:border-gray-700 mb-2">
                                  <input
                                    type="checkbox"
                                    checked={(() => {
                                      return activeCheckerAccounts.length > 0 &&
                                        activeCheckerAccounts.every(acc => selectedActiveCheckerAccountIds.includes(acc._id));
                                    })()}
                                    onChange={handleSelectAllActiveCheckerAccounts}
                                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                  />
                                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Select All Available
                                  </span>
                                </label>
                                
                                {/* Groups Section */}
                                {(() => {
                                  const accountGroups = [...new Set(activeCheckerAccounts
                                    .map(acc => acc.group)
                                    .filter(g => g != null && String(g).trim() !== '')
                                    .map(g => String(g).trim()))];
                                  
                                  if (accountGroups.length === 0) return null;
                                  
                                  return (
                                    <div className="space-y-1 mb-3 pb-3 border-b border-gray-200 dark:border-gray-700">
                                      <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                                        Groups
                                      </div>
                                      {accountGroups.sort().map((groupName) => {
                                        const groupAccounts = activeCheckerAccounts.filter(acc => {
                                          if (!acc.group) return false;
                                          return String(acc.group).trim() === groupName;
                                        });
                                        const groupAccountIds = groupAccounts.map(acc => acc._id);
                                        const allGroupSelected = groupAccountIds.length > 0 && 
                                          groupAccountIds.every(id => selectedActiveCheckerAccountIds.includes(id));
                                        const isExpanded = expandedActiveCheckerGroups.has(groupName);
                                        
                                        return (
                                          <div key={groupName} className="space-y-1">
                                            <div className="flex items-center justify-between">
                                              <div className="flex items-center flex-1 gap-2">
                                                <button
                                                  type="button"
                                                  onClick={() => handleToggleActiveCheckerGroup(groupName)}
                                                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                                >
                                                  {isExpanded ? (
                                                    <ChevronDownIcon className="h-4 w-4" />
                                                  ) : (
                                                    <ChevronDownIcon className="h-4 w-4 transform -rotate-90" />
                                                  )}
                                                </button>
                                                <input
                                                  type="checkbox"
                                                  checked={allGroupSelected}
                                                  onChange={() => handleSelectActiveCheckerGroup(groupName)}
                                                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                                />
                                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                                  {groupName} ({groupAccounts.length})
                                                </span>
                                              </div>
                                            </div>
                                            {isExpanded && (
                                              <div className="ml-6 space-y-1 mt-1">
                                                {groupAccounts.map((account) => {
                                                  return (
                                                    <label key={account._id} className="flex items-center justify-between">
                                                      <div className="flex items-center flex-1">
                                                        <input
                                                          type="checkbox"
                                                          checked={selectedActiveCheckerAccountIds.includes(account._id)}
                                                          onChange={() => toggleActiveCheckerAccountSelection(account._id)}
                                                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                                        />
                                                        <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                                                          {account.displayName || account.username}
                                                        </span>
                                                      </div>
                                                      {!account.hasValidSession && (
                                                        <span className="ml-2 text-xs text-amber-600 dark:text-amber-400" title="Account needs login test">
                                                          ⚠️
                                                        </span>
                                                      )}
                                                      {account.hasValidSession && (
                                                        <span className="ml-2 text-xs text-green-600 dark:text-green-400" title="Account has valid session data">
                                                          ✓ Ready
                                                        </span>
                                                      )}
                                                    </label>
                                                  );
                                                })}
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  );
                                })()}
                                
                                {/* Ungrouped Accounts */}
                                {(() => {
                                  const ungroupedAccounts = activeCheckerAccounts.filter(acc => !acc.group || acc.group.trim() === '');
                                  if (ungroupedAccounts.length === 0) return null;
                                  
                                  return (
                                    <div className="space-y-1">
                                      {(() => {
                                        const accountGroups = [...new Set(activeCheckerAccounts
                                          .map(acc => acc.group)
                                          .filter(g => g != null && String(g).trim() !== ''))];
                                        if (accountGroups.length > 0) {
                                          return (
                                            <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                                              Ungrouped
                                            </div>
                                          );
                                        }
                                        return null;
                                      })()}
                                      {ungroupedAccounts.map((account) => {
                                        return (
                                          <label key={account._id} className="flex items-center justify-between">
                                            <div className="flex items-center flex-1">
                                              <input
                                                type="checkbox"
                                                checked={selectedActiveCheckerAccountIds.includes(account._id)}
                                                onChange={() => toggleActiveCheckerAccountSelection(account._id)}
                                                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                              />
                                              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                                                {account.displayName || account.username}
                                              </span>
                                            </div>
                                            {!account.hasValidSession && (
                                              <span className="ml-2 text-xs text-amber-600 dark:text-amber-400" title="Account needs login test">
                                                ⚠️
                                              </span>
                                            )}
                                            {account.hasValidSession && (
                                              <span className="ml-2 text-xs text-green-600 dark:text-green-400" title="Account has valid session data">
                                                ✓ Ready
                                              </span>
                                            )}
                                          </label>
                                        );
                                      })}
                                    </div>
                                  );
                                })()}
                              </div>
                            )}
                          </div>
                          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                            Active checker accounts will be used in parallel to check user activity, making the process much faster.
                          </p>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Source Accounts Section */}
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={sourcingEnabled}
                        onChange={(e) => setSourcingEnabled(e.target.checked)}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center">
                        <SparklesIcon className="h-4 w-4 mr-1" />
                        Auto-discover target usernames (Source)
                      </span>
                    </label>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 ml-6">
                      Source accounts will browse the For You feed and automatically add new target usernames matching your follower range criteria.
                    </p>

                    {sourcingEnabled && (
                      <div className="mt-3 ml-6 space-y-3">
                        <div className="grid grid-cols-3 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Min Followers</label>
                            <input
                              type="number"
                              value={sourcingConfig.minFollowers}
                              onChange={(e) => setSourcingConfig(prev => ({ ...prev, minFollowers: parseInt(e.target.value) || 0 }))}
                              className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                              min={0}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Max Followers</label>
                            <input
                              type="number"
                              value={sourcingConfig.maxFollowers}
                              onChange={(e) => setSourcingConfig(prev => ({ ...prev, maxFollowers: parseInt(e.target.value) || 0 }))}
                              className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                              min={0}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Pause at</label>
                            <input
                              type="number"
                              value={sourcingConfig.maxPendingTargets}
                              onChange={(e) => setSourcingConfig(prev => ({ ...prev, maxPendingTargets: parseInt(e.target.value) || 1 }))}
                              className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                              min={1}
                              max={100}
                            />
                            <p className="text-[10px] text-gray-400 mt-0.5">pending targets</p>
                          </div>
                        </div>

                        {/* Grok AI Filter */}
                        <label className="flex items-center mt-1">
                          <input
                            type="checkbox"
                            checked={sourcingConfig.grokFilter}
                            onChange={(e) => setSourcingConfig(prev => ({ ...prev, grokFilter: e.target.checked }))}
                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                          <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                            Filter with Grok AI
                          </span>
                          <span className="ml-1 text-xs text-gray-400">(skip male / Indian / Arabic accounts)</span>
                        </label>

                        <label className="flex items-center mt-1">
                          <input
                            type="checkbox"
                            checked={sourcingConfig.containsLinks}
                            onChange={(e) => setSourcingConfig(prev => ({ ...prev, containsLinks: e.target.checked }))}
                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                          <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                            Contains links
                          </span>
                          <span className="ml-1 text-xs text-gray-400">(bio URL entities, website field, or http / t.co in bio text)</span>
                        </label>

                        <label className="flex items-start mt-1">
                          <input
                            type="checkbox"
                            checked={sourcingConfig.bioWhitelist}
                            onChange={(e) => setSourcingConfig(prev => ({ ...prev, bioWhitelist: e.target.checked }))}
                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 mt-0.5"
                          />
                          <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                            <span className="font-medium">Bio whitelist</span>
                            <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                              Only add targets whose bio contains at least one built-in phrase (OR match). Multi-word phrases use substring match; short single words use whole-word match.
                            </span>
                          </span>
                        </label>

                        {/* Source Account Selection */}
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                              Select Source Accounts <span className="text-red-500">*</span>
                            </label>
                            <span className="text-sm text-gray-500 dark:text-gray-400">
                              {selectedSourceAccountIds.length} of {sourceAccounts.length} selected
                            </span>
                          </div>
                          <div className="border border-gray-300 dark:border-gray-600 rounded-md p-3 max-h-48 overflow-y-auto">
                            {sourceAccounts.length === 0 ? (
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                No accounts with "source" tag found. Tag accounts in the Accounts page first.
                              </p>
                            ) : (
                              <div className="space-y-2">
                                <label className="flex items-center gap-2 cursor-pointer pb-2 border-b border-gray-200 dark:border-gray-700 mb-2">
                                  <input
                                    type="checkbox"
                                    checked={(() => {
                                      const selectable = sourceAccounts.filter(acc => acc.hasValidSession && !acc.inUse);
                                      return selectable.length > 0 && selectable.every(acc => selectedSourceAccountIds.includes(acc._id));
                                    })()}
                                    onChange={handleSelectAllSourceAccounts}
                                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                  />
                                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Select All Available</span>
                                </label>

                                {/* Groups */}
                                {(() => {
                                  const groups = [...new Set(sourceAccounts.map(acc => acc.group).filter(g => g != null && String(g).trim() !== '').map(g => String(g).trim()))];
                                  if (groups.length === 0) return null;
                                  return (
                                    <div className="space-y-1 mb-3 pb-3 border-b border-gray-200 dark:border-gray-700">
                                      <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Groups</div>
                                      {groups.sort().map((groupName) => {
                                        const grpAccounts = sourceAccounts.filter(acc => acc.group && String(acc.group).trim() === groupName);
                                        const selectable = grpAccounts.filter(acc => acc.hasValidSession === true && acc.inUse !== true);
                                        const grpIds = selectable.map(acc => acc._id);
                                        const allSel = selectable.length > 0 && grpIds.every(id => selectedSourceAccountIds.includes(id));
                                        const isExp = expandedSourceGroups.has(groupName);
                                        return (
                                          <div key={groupName} className="space-y-1">
                                            <div className="flex items-center flex-1 gap-2">
                                              <button type="button" onClick={() => handleToggleSourceGroup(groupName)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                                                <ChevronDownIcon className={`h-4 w-4 ${!isExp ? 'transform -rotate-90' : ''}`} />
                                              </button>
                                              <input type="checkbox" checked={allSel} onChange={() => handleSelectSourceGroup(groupName)} disabled={selectable.length === 0} className={`rounded border-gray-300 text-primary-600 focus:ring-primary-500 ${selectable.length === 0 ? 'opacity-50 cursor-not-allowed' : ''}`} />
                                              <span className={`text-sm font-medium ${selectable.length === 0 ? 'text-gray-400 dark:text-gray-500' : 'text-gray-700 dark:text-gray-300'}`}>{groupName} ({selectable.length}/{grpAccounts.length})</span>
                                            </div>
                                            {isExp && (
                                              <div className="ml-6 space-y-1 mt-1">
                                                {grpAccounts.map(account => {
                                                  const valid = account.hasValidSession === true;
                                                  const inUse = account.inUse === true;
                                                  const disabled = !valid || inUse;
                                                  return (
                                                    <label key={account._id} className="flex items-center justify-between">
                                                      <div className="flex items-center flex-1">
                                                        <input type="checkbox" checked={selectedSourceAccountIds.includes(account._id)} onChange={() => toggleSourceAccountSelection(account._id)} className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" disabled={disabled} />
                                                        <span className={`ml-2 text-sm ${disabled ? 'text-gray-400 dark:text-gray-500' : 'text-gray-700 dark:text-gray-300'}`}>{account.displayName || account.username}</span>
                                                      </div>
                                                      {!valid && <span className="ml-2 text-xs text-red-600 dark:text-red-400">No Session</span>}
                                                      {valid && inUse && <span className="ml-2 text-xs text-orange-600 dark:text-orange-400">In Use</span>}
                                                      {valid && !inUse && <span className="ml-2 text-xs text-green-600 dark:text-green-400">Ready</span>}
                                                    </label>
                                                  );
                                                })}
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  );
                                })()}

                                {/* Ungrouped */}
                                {(() => {
                                  const ungrouped = sourceAccounts.filter(acc => !acc.group || acc.group.trim() === '');
                                  if (ungrouped.length === 0) return null;
                                  return (
                                    <div className="space-y-1">
                                      {sourceAccounts.some(acc => acc.group && acc.group.trim() !== '') && (
                                        <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Ungrouped</div>
                                      )}
                                      {ungrouped.map(account => {
                                        const valid = account.hasValidSession === true;
                                        const inUse = account.inUse === true;
                                        const disabled = !valid || inUse;
                                        return (
                                          <label key={account._id} className="flex items-center justify-between">
                                            <div className="flex items-center flex-1">
                                              <input type="checkbox" checked={selectedSourceAccountIds.includes(account._id)} onChange={() => toggleSourceAccountSelection(account._id)} className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" disabled={disabled} />
                                              <span className={`ml-2 text-sm ${disabled ? 'text-gray-400 dark:text-gray-500' : 'text-gray-700 dark:text-gray-300'}`}>{account.displayName || account.username}</span>
                                            </div>
                                            {!valid && <span className="ml-2 text-xs text-red-600 dark:text-red-400">No Session</span>}
                                            {valid && inUse && <span className="ml-2 text-xs text-orange-600 dark:text-orange-400">In Use</span>}
                                            {valid && !inUse && <span className="ml-2 text-xs text-green-600 dark:text-green-400">Ready</span>}
                                          </label>
                                        );
                                      })}
                                    </div>
                                  );
                                })()}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-50 dark:bg-gray-700/50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse border-t border-gray-200 dark:border-gray-600">
                 <button
                   onClick={handleStartScraping}
                   disabled={!taskName.trim() || (!usernames.trim() && !sourcingEnabled) || !tag.trim() || selectedAccountIds.length === 0 || ((filters.filterActiveLastMonth || filters.filterCountryWhitelist) && selectedActiveCheckerAccountIds.length === 0) || (filters.filterCountryWhitelist && allowedCountriesList.length === 0) || (sourcingEnabled && selectedSourceAccountIds.length === 0)}
                   className="w-full sm:w-auto sm:ml-3 inline-flex justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                 >
                  <PlayIcon className="h-4 w-4 mr-2" />
                  Start Scraping
                </button>
                <button
                  onClick={() => setShowStartModal(false)}
                  className="mt-3 w-full sm:mt-0 sm:w-auto inline-flex justify-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Permission Denied Modal */}
      {showPermissionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center justify-center w-12 h-12 mx-auto bg-yellow-100 dark:bg-yellow-900/20 rounded-full mb-4">
              <svg className="w-6 h-6 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white text-center mb-2">
              Permission Required
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 text-center mb-6">
              You do not have permission to use the Scraper feature. Please contact support to enable this feature for your account.
            </p>
            <button
              onClick={() => setShowPermissionModal(false)}
              className="w-full px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Scraper;
